import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  Paper,
  IconButton,
  Popover,
  Tooltip,
  TextField,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar,
  Divider,
} from "@mui/material";
import moment from "moment";
import { styled } from "@mui/material/styles";
import {
  AccessTime,
  LocationOn as LocationIcon,
  EmojiEmotions,
  Add,
  MoreVert,
  Forward,
  Reply,
  Edit,
  Delete,
  Info,
  Reply as ReplyIcon,
  DoneAll,
  Done,
  Close as CloseIcon,
  AudioFile as AudioIcon,
  InsertDriveFile as FileIcon,
  Download as DownloadIcon,
  Person as PersonIcon,
} from "@mui/icons-material";
import { IMAGE_URL, MEDIA_URL, ApiEndPoints, AUDIO_URL, getImageDisplayUrl } from "src/network/endpoints";
import { useSocketContext } from "src/context/SocketContext";
import { axiosInstance } from "src/network/adapter";
import { toast } from "react-hot-toast";
import { useSelector } from "react-redux";
import authConfig from "src/configs/auth";
import currencyData from "src/constants/currencyData.json";
import MessageTimer from "./MessageTimer";
import MessageInfoDialog from "./MessageInfoDialog";
import {
  AddRemoveMessage,
  PaymentResponseMessage,
  PaymentRequestSplitEvenly,
  MediaMessage,
  PaymentRequestUpfrontMessage,
  PaymentRequestPayForAllMessage,
  PaymentRequestSplitPerPersonMessage,
  OrderMessage,
  OrderCancellationRequestMessage,
  PaymentRetryOptionMessage,
  PaymentRequestIndividualMessage,
  PaymentRequestIndividualRemainingAmountMessage,
  PaymentOptionMessage,
  PaymentRequestMessage,
  PaymentBillSplitEvenlyMessage,
  PaymentRequestBillMessage,
  VoiceMessage,
  PaymentCompletionMessage,
  UpfrontPaymentMessage,
  MessageBubbleContainer as MessageBubbleContainerStyled,
  MessageContent as MessageContentStyled,
  MediaGrid as MediaGridStyled,
  MediaItem as MediaItemStyled,
  MediaImage as MediaImageStyled,
  MediaVideo as MediaVideoStyled,
  MediaOverlay as MediaOverlayStyled,
  MediaCounter as MediaCounterStyled,
  EmojiPicker as EmojiPickerStyled,
  EmojiButton as EmojiButtonStyled,
  SmileButton as SmileButtonStyled,
  EdgeSmileButton as EdgeSmileButtonStyled,
} from "./MessageTypes";

// styled component for the message bubble container
// use $fitContent when message is location so bubble shrinks to content (no empty right space)
const MessageBubbleContainer = styled(Box)(({ theme, isOwn, $fitContent }) => ({
  maxWidth: "70%",
  ...($fitContent && { width: "fit-content", maxWidth: "100%" }),
  alignSelf: isOwn ? "flex-end" : "flex-start",
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(0.5),
  marginLeft: isOwn ? "auto" : "0",
  marginRight: isOwn ? "0" : "auto",
  marginBottom: theme.spacing(1),
  [theme.breakpoints.down("md")]: {
    maxWidth: $fitContent ? "100%" : "70%",
  },
  [theme.breakpoints.down("sm")]: {
    maxWidth: $fitContent ? "100%" : "75%",
    marginBottom: theme.spacing(0.5),
  },
}));

// styled component for the message content
// handles different message types: normal messages, own messages, order messages, payment messages, and request messages
const MessageContent = styled(Paper)(
  ({
    theme,
    isOwn,
    isTemp,
    isOrder,
    isPayment,
    isRequest,
    isCancellation,
    idDraft,
    isDisabled,
  }) => ({
    padding: isOrder
      ? theme.spacing(3, 4) // more padding for order messages to make them look more spacious
      : isCancellation
      ? theme.spacing(3, 4) // more padding for cancellation messages to make them look more spacious
      : isPayment
      ? theme.spacing(2.5, 3) // slightly more padding for payment messages
      : isRequest
      ? theme.spacing(2.5, 3) // slightly more padding for request messages
      : theme.spacing(2, 3), // normal padding for other messages
    backgroundColor: isDisabled
      ? "#f3f4f6" // gray background for disabled messages
      : isOrder
      ? idDraft
        ? "#fc5454"
        : "#FFD700" // yellow background for order messages to match design requirements
      : isCancellation
      ? "#AFAFAF" // gray background for cancellation messages as requested
      : isPayment
      ? isOwn
        ? "#7C3AED" // purple for own payment messages
        : "#f8fafc" // light gray background for payment messages from others
      : isRequest
      ? isOwn
        ? "#7C3AED" // purple for own request messages
        : "#f3f4f6" // light gray background for request messages from others
      : isOwn
      ? "#7C3AED" // purple for own messages
      : "#efe5fd", // light purple for others
    color: isDisabled
      ? "#9ca3af" // gray text for disabled messages
      : isOrder
      ? "#000000" // black text for order messages for better contrast on yellow background
      : isCancellation
      ? "#000000" // black text for cancellation messages for better contrast on gray background
      : isPayment
      ? isOwn
        ? "#FFFFFF" // white text for own payment messages
        : "#374151" // dark gray text for payment messages from others
      : isRequest
      ? isOwn
        ? "#FFFFFF" // white text for own request messages
        : "#374151" // dark gray text for request messages from others
      : isOwn
      ? "#FFFFFF" // white text for own messages
      : "#7C3AED", // purple text for others
    opacity: isDisabled ? 0.6 : 1, // reduce opacity for disabled messages
    borderRadius: theme.spacing(2),
    boxShadow: isOrder
      ? "0 4px 12px rgba(0,0,0,0.15)" // enhanced shadow for order messages
      : isCancellation
      ? "0 4px 12px rgba(0,0,0,0.15)" // enhanced shadow for cancellation messages
      : isPayment
      ? "0 3px 10px rgba(0,0,0,0.12)" // enhanced shadow for payment messages
      : isRequest
      ? "0 3px 10px rgba(0,0,0,0.12)" // enhanced shadow for request messages
      : "0 2px 8px rgba(0,0,0,0.1)", // normal shadow for other messages
    [theme.breakpoints.down("sm")]: {
      padding: isOrder
        ? theme.spacing(2, 2.5) // adjusted mobile padding for order messages
        : isCancellation
        ? theme.spacing(2, 2.5) // adjusted mobile padding for cancellation messages
        : isPayment
        ? theme.spacing(2, 2.5) // adjusted mobile padding for payment messages
        : isRequest
        ? theme.spacing(2, 2.5) // adjusted mobile padding for request messages
        : theme.spacing(1, 1.5), // normal mobile padding for other messages
      borderRadius: theme.spacing(1.5),
    },
  })
);

// styled components for media display
const MediaGrid = styled(Box)(({ theme, count }) => ({
  display: "grid",
  gap: theme.spacing(0.5),
  borderRadius: theme.spacing(1),
  overflow: "hidden",
  cursor: "pointer",
  "&:hover": {
    opacity: 0.9,
  },
  ...(count === 1 && {
    gridTemplateColumns: "1fr",
    maxWidth: "200px",
  }),
  ...(count === 2 && {
    gridTemplateColumns: "1fr 1fr",
    maxWidth: "300px",
  }),
  ...(count === 3 && {
    gridTemplateColumns: "1fr 1fr",
    gridTemplateRows: "1fr 1fr",
    maxWidth: "300px",
    "& > *:first-of-type": {
      gridColumn: "1 / -1",
    },
  }),
  ...(count === 4 && {
    gridTemplateColumns: "1fr 1fr",
    gridTemplateRows: "1fr 1fr",
    maxWidth: "300px",
  }),
  ...(count > 4 && {
    gridTemplateColumns: "1fr 1fr",
    gridTemplateRows: "1fr 1fr",
    maxWidth: "300px",
  }),
  [theme.breakpoints.down("md")]: {
    ...(count === 1 && {
      maxWidth: "180px",
    }),
    ...(count === 2 && {
      maxWidth: "260px",
    }),
    ...(count === 3 && {
      maxWidth: "260px",
    }),
    ...(count === 4 && {
      maxWidth: "260px",
    }),
    ...(count > 4 && {
      maxWidth: "260px",
    }),
  },
  [theme.breakpoints.down("sm")]: {
    ...(count === 1 && {
      maxWidth: "160px",
    }),
    ...(count === 2 && {
      maxWidth: "240px",
    }),
    ...(count === 3 && {
      maxWidth: "240px",
    }),
    ...(count === 4 && {
      maxWidth: "240px",
    }),
    ...(count > 4 && {
      maxWidth: "240px",
    }),
  },
}));

const MediaItem = styled(Box)(({ theme }) => ({
  position: "relative",
  width: "100%",
  height: "100%",
  minHeight: "80px",
  backgroundColor: theme.palette.grey[200],
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  overflow: "hidden",
  [theme.breakpoints.down("sm")]: {
    minHeight: "70px",
  },
}));

const MediaImage = styled("img")({
  width: "100%",
  height: "100%",
  objectFit: "cover",
});

const MediaVideo = styled("video")({
  width: "100%",
  height: "100%",
  objectFit: "cover",
});

const MediaOverlay = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: "rgba(0, 0, 0, 0.6)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "white",
  fontSize: "1.5rem",
  fontWeight: "bold",
  [theme.breakpoints.down("sm")]: {
    fontSize: "1.25rem",
  },
}));

const MediaCounter = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: theme.spacing(1),
  right: theme.spacing(1),
  backgroundColor: "rgba(0, 0, 0, 0.7)",
  color: "white",
  padding: theme.spacing(0.5, 1),
  borderRadius: theme.spacing(1),
  fontSize: "0.875rem",
  fontWeight: "bold",
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(0.25, 0.75),
    fontSize: "0.8rem",
  },
}));

// styled component for emoji picker container
const EmojiPicker = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  padding: theme.spacing(1),
  backgroundColor: "#ffffff",
  borderRadius: theme.spacing(1),
  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
  border: "1px solid #e5e7eb",
}));

// styled component for emoji button
const EmojiButton = styled(IconButton)(({ theme }) => ({
  width: "32px",
  height: "32px",
  fontSize: "1.2rem",
  cursor: "pointer",
  transition: "all 0.2s ease",
  "&:hover": {
    transform: "scale(1.1)",
    backgroundColor: "#f3f4f6",
  },
}));

// styled component for smile icon button
const SmileButton = styled(IconButton)(({ theme, isOwn }) => ({
  width: "24px",
  height: "24px",
  color: isOwn ? "#ffffff" : "#7C3AED",
  opacity: 0.7,
  transition: "all 0.2s ease",
  "&:hover": {
    opacity: 1,
    transform: "scale(1.1)",
  },
  [theme.breakpoints.down("sm")]: {
    width: "20px",
    height: "20px",
  },
}));

// styled component for edge smile button (left or right)
const EdgeSmileButton = styled(IconButton)(({ theme, position }) => ({
  position: "absolute",
  top: "50%",
  transform: "translateY(-50%)",
  width: "20px", // reduced from 24px
  height: "20px", // reduced from 24px
  backgroundColor: "#6B7280", // gray background
  color: "#ffffff", // white icon
  border: "2px solid #ffffff",
  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
  opacity: 0.9,
  transition: "all 0.2s ease",
  zIndex: 1,
  "&:hover": {
    opacity: 1,
    transform: "translateY(-50%) scale(1.1)",
    backgroundColor: "#4B5563", // darker gray on hover
  },
  ...(position === "left" && {
    left: "-28px", // increased by 5px more (24px - 20px/2 = 20px spacing)
  }),
  ...(position === "right" && {
    right: "-28px", // increased by 5px more (24px - 20px/2 = 20px spacing)
  }),
  [theme.breakpoints.down("sm")]: {
    width: "18px", // reduced from 20px
    height: "18px", // reduced from 20px
    ...(position === "left" && {
      left: "-20px", // increased by 5px more on mobile (20px - 18px/2 = 17px spacing)
    }),
    ...(position === "right" && {
      right: "-20px", // increased by 5px more on mobile (20px - 18px/2 = 17px spacing)
    }),
  },
}));

/**
 * MessageBubble Component
 *
 * This component displays individual chat messages with support for:
 * - Text messages with proper styling for own vs other messages
 * - Media attachments (images, videos) with grid layout
 * - Location information display
 * - Message status indicators (delivered, read, typing)
 * - Timestamp display
 * - Username display for other users' messages
 * - Emoji reactions with smile icon button
 *
 * @param {Object} props - Component props
 * @param {Object} props.message - The message object containing all message data
 * @param {boolean} props.isOwn - Whether this message belongs to the current user
 * @param {Function} props.onMediaClick - Function to handle media click events
 * @param {Function} props.onEmojiReaction - Function to handle emoji reaction selection
 */
const MessageBubble = ({
  message,
  isOwn,
  onMediaClick,
  onEmojiReaction,
  onForwardMessage,
  onReplyToMessage,
  onViewOrder,
  onOpenPaymentDialog,
  onPaymentResponse,
  onSearchMenuItemByName,
  selectedContact,
  conversationInfo,
  isCurrentUserOwnerOrTeamMember = false,
  enableInAppWalletPayments = true,
  businessLocationName = "",
  onOpenNotes,
  onViewLocation,
}) => {

// Removed console.log to prevent spam
  // get theme for styling
  const theme = useTheme();

  // get socket context for message operations
  const { emit } = useSocketContext();

  // get current user ID for checking delete status
  const currentUser = useSelector((state) => state.auth.user);
  const storedUserId =
    typeof window !== "undefined"
      ? window.localStorage.getItem(authConfig.storageUserIdKeyName)
      : null;
  const currentUserId = currentUser?.userId ?? storedUserId;

  // state for emoji picker popover
  const [anchorEl, setAnchorEl] = useState(null);
  const [activeSide, setActiveSide] = useState(null); // 'left' or 'right'
  // state for extended emoji picker
  const [showExtendedEmoji, setShowExtendedEmoji] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("Smileys");
  const [searchQuery, setSearchQuery] = useState("");

  // state for options menu popover
  const [optionsAnchorEl, setOptionsAnchorEl] = useState(null);

  // state for edit mode
  const [isEditing, setIsEditing] = useState(false);
  const [editText, setEditText] = useState(message.text || "");

  // state for timer functionality
  const [timeRemaining, setTimeRemaining] = useState(30);
  const [timerActive, setTimerActive] = useState(false);
  const [userHasResponded, setUserHasResponded] = useState(false);
  const [autoResponseTriggered, setAutoResponseTriggered] = useState(false);
  const [timerExpired, setTimerExpired] = useState(false);
  const timerRef = useRef(null);
  const hasInitialized = useRef(false);

  // state for wallet payment disabled dialog
  const [walletPaymentDialogOpen, setWalletPaymentDialogOpen] = useState(false);
  const [pendingPaymentAction, setPendingPaymentAction] = useState(null);

  // state for message info dialog (read/delivered status)
  const [messageInfoDialogOpen, setMessageInfoDialogOpen] = useState(false);


  const apiCalled = useRef(false);

  // Helper function to get currency symbol from currency code
  const getCurrencySymbol = (currencyCode = "USD") => {
    if (!currencyCode || !currencyData?.currencies) {
      return "$"; // Default to USD if currency data is not available
    }
    const currency = currencyData.currencies.find(
      (c) => c.code === currencyCode.toUpperCase()
    );
    return currency?.symbol || "$"; // Default to $ if currency not found
  };

  // Helper function to check if replyTo is valid (not empty object)
  const isValidReplyTo = (replyTo, replyToText, replyToSender, replyToAttachments) => {
    // Check if replyTo exists and is not null/undefined
    if (!replyTo || replyTo === null || replyTo === undefined) {
      return false;
    }
    
    // If replyTo is an object, check if it's not empty
    if (typeof replyTo === "object" && Object.keys(replyTo).length === 0) {
      return false;
    }
    
    // If replyTo is a string (non-empty), it's a valid ID
    if (typeof replyTo === "string" && replyTo.trim() !== "") {
      return true;
    }
    
    // If replyTo is an object with _id property, it's valid
    if (typeof replyTo === "object" && replyTo._id) {
      return true;
    }
    
    // Also check if we have any reply data (text, sender, or attachments) to indicate it's a valid reply
    const hasReplyData = 
      (replyToText && replyToText.trim() !== "") ||
      (replyToSender && replyToSender.trim() !== "") ||
      (Array.isArray(replyToAttachments) && replyToAttachments.length > 0);
    
    return hasReplyData;
  };

  // Helper function to format mentions for display
  // Keep the original text as is (mentions come from server in <:username> format)
  const formatMentionsForDisplay = (text) => {
    if (!text) {
      return text;
    }

    // Return text as is - mentions are already in <:username> format from server
    return text;
  };

  // Helper function to parse numbered menu items from AI-generated text
  // Detects patterns like "1. Item Name", "2. Item Name", etc.
  const parseNumberedMenuItems = (text) => {
    if (!text || !onSearchMenuItemByName) {
      return null;
    }

    // Check if message contains menu-like content (has numbered list with at least 2 items)
    // Look for pattern of numbered items (1., 2., 3., etc.) to determine if it's a menu list
    const hasNumberedList = /\d+\.\s+\*\*/.test(text) || (/\d+\.\s+/.test(text) && (text.match(/\d+\.\s+/g) || []).length >= 2);
    if (!hasNumberedList) {
      return null;
    }

    // Pattern to match numbered menu items with optional bold formatting
    // Matches: "1. **Item Name**" or "1. Item Name" followed by description or newline
    const numberedItemPattern = /(\d+\.\s*)\*\*([^*]+?)\*\*|(\d+\.\s*)([A-Za-z][^\n:]*?)(?=\n|:|$|\d+\.|\*\*)/g;
    const items = [];
    let match;

    while ((match = numberedItemPattern.exec(text)) !== null) {
      const itemNumber = match[1] || match[3] || '';
      let itemName = (match[2] || match[4] || '').trim();
      
      // Clean up item name
      itemName = itemName
        .replace(/^\*\*|\*\*$/g, '') // Remove bold markers if any
        .split(':')[0] // Remove everything after colon
        .split('- Description')[0] // Remove "Description:" prefix if present
        .trim();

      // Only process if we have a valid item name (at least 2 characters)
      if (itemName && itemName.length >= 2) {
        items.push({
          startIndex: match.index,
          endIndex: match.index + match[0].length,
          number: itemNumber.trim(),
          name: itemName,
          fullMatch: match[0],
        });
      }
    }

    // Only return items if we found at least 2 (likely a menu list)
    return items.length >= 2 ? items : null;
  };

  // Parse AI generated menu sections that follow markdown style headings and bullet points
  const parseAiMenuSections = (text) => {
    if (!text) {
      return null;
    }

    const lines = text.split("\n");
    const sections = [];
    const introLines = [];
    const outroLines = [];
    let menuStarted = false;
    let currentSection = null;
    let currentItem = null;
    let signalCount = 0;
    let pendingItemName = null;

    const flushCurrentItem = () => {
      if (currentItem && currentItem.name) {
        if (!currentSection) {
          currentSection = {
            title: "Menu",
            items: [],
          };
          sections.push(currentSection);
        }

        currentItem.imageUrls = Array.from(new Set(currentItem.imageUrls));
        currentSection.items.push(currentItem);
      }
      currentItem = null;
    };

    lines.forEach((rawLine) => {
      const trimmedLine = rawLine.trim();

      if (!trimmedLine) {
        flushCurrentItem();
        return;
      }

      if (/^-{3,}$/.test(trimmedLine)) {
        flushCurrentItem();
        menuStarted = true;
        return;
      }

      const headingMatch = trimmedLine.match(/^#{2,4}\s+\**(.+?)\**\s*$/);
      if (headingMatch) {
        flushCurrentItem();
        const title = headingMatch[1].replace(/^\*\*|\*\*$/g, "").trim();
        currentSection = {
          title: title || "Menu",
          items: [],
        };
        sections.push(currentSection);
        menuStarted = true;
        return;
      }

      // Track potential item name from standalone bold text (e.g., "**Pan Cakes**")
      if (!trimmedLine.startsWith("-")) {
        const standaloneNameMatch = trimmedLine.match(/\*\*([^*]+)\*\*/);
        if (standaloneNameMatch) {
          pendingItemName = standaloneNameMatch[1].trim();
        }
      }

      const detailMatch = trimmedLine.match(
        /^[-•*]\s*(description|desc|price|allergens|ingredients|protein|calories|serving size|serving|note|extras|image|photo|pic)\s*:\s*(.+)$/i
      );
      if (detailMatch) {
        if (!currentItem) {
          currentItem = {
            name: pendingItemName || "Menu Item",
            description: "",
            price: "",
            extra: [],
            imageUrls: [],
          };
        }

        const label = detailMatch[1].toLowerCase();
        const value = detailMatch[2].trim();

        if (label === "description" || label === "desc") {
          currentItem.description = value.replace(/^\*\*|\*\*$/g, "");
        } else if (label === "price") {
          currentItem.price = value.replace(/^\*\*|\*\*$/g, "");
        } else if (label === "image" || label === "photo" || label === "pic") {
          const inlineImageMatch = value.match(/!\[[^\]]*\]\(([^)]+)\)/);
          if (inlineImageMatch) {
            currentItem.imageUrls.push(inlineImageMatch[1].trim());
          }
        } else {
          currentItem.extra.push({
            label,
            value: value.replace(/^\*\*|\*\*$/g, ""),
          });
        }

        signalCount++;
        menuStarted = true;
        return;
      }

      const itemMatch = trimmedLine.match(/^[-•*]\s+\**(.+?)\**\s*$/);
      if (itemMatch) {
        flushCurrentItem();
        const name = itemMatch[1].replace(/^\*\*|\*\*$/g, "").trim();
        if (!name) {
          return;
        }
        currentItem = {
          name,
          description: "",
          price: "",
          extra: [],
          imageUrls: [],
        };
        menuStarted = true;
        return;
      }

      const imageMatch = rawLine.match(/!\[([^\]]*)\]\(([^)]+)\)/);
      if (imageMatch) {
        const [, altText = ""] = imageMatch;
        const imageUrl = imageMatch[2]?.trim();
        if (!currentItem) {
          currentItem = {
            name: pendingItemName || altText || "Item",
            description: "",
            price: "",
            extra: [],
            imageUrls: [],
          };
        }
        if (imageUrl) {
          currentItem.imageUrls.push(imageUrl);
          signalCount++;
        }
        menuStarted = true;
        return;
      }

      if (currentItem) {
        currentItem.extra.push({
          label: "note",
          value: trimmedLine,
        });
        return;
      }

      if (!menuStarted) {
        introLines.push(rawLine);
      } else {
        outroLines.push(rawLine);
      }
    });

    flushCurrentItem();

    const filteredSections = sections
      .map((section) => ({
        ...section,
        items: section.items.filter((item) => item.name),
      }))
      .filter((section) => section.items.length > 0);

    const totalItems = filteredSections.reduce(
      (sum, section) => sum + section.items.length,
      0
    );

    if (totalItems < 1 || signalCount < 1) {
      return null;
    }

    return {
      introText: introLines.join("\n").trim(),
      sections: filteredSections,
      outroText: outroLines.join("\n").trim(),
    };
  };

  // Parse single item detail blocks (description, price, etc.) even without explicit sections
  const parseSingleMenuItem = (text) => {
    if (!text) {
      return null;
    }

    const lines = text.split("\n");
    const introLines = [];
    const outroLines = [];
    const item = {
      name: null,
      description: "",
      price: "",
      extra: [],
      imageUrls: [],
    };

    // Attempt to derive the item name from a bolded portion next to mention
    const nameFromBoldMention = text.match(/@[\w\s.]*\*\*([^*]+)\*\*/);
    if (nameFromBoldMention) {
      item.name = nameFromBoldMention[1].trim();
    }

    let detailsStarted = false;
    let detailCount = 0;

    lines.forEach((rawLine) => {
      const trimmedLine = rawLine.trim();
      if (!trimmedLine) {
        return;
      }

      const detailMatch = trimmedLine.match(
        /^[-•*]\s*\*\*?([^*:]+)\*\*?\s*:\s*(.+)$/i
      );

      if (detailMatch) {
        detailsStarted = true;
        detailCount += 1;
        const label = detailMatch[1].trim().toLowerCase();
        const rawValue = detailMatch[2].trim();
        const cleanedValue = rawValue.replace(/^\*\*|\*\*$/g, "");

        if (!item.name && label === "name") {
          item.name = cleanedValue;
        } else if (label === "description" || label === "desc") {
          item.description = cleanedValue;
        } else if (label === "price") {
          item.price = cleanedValue;
        } else if (label === "image" || label === "photo" || label === "pic") {
          const inlineImageMatch = rawValue.match(/!\[[^\]]*\]\(([^)]+)\)/);
          if (inlineImageMatch) {
            item.imageUrls.push(inlineImageMatch[1].trim());
          } else if (cleanedValue.startsWith("http")) {
            item.imageUrls.push(cleanedValue);
          }
        } else {
          item.extra.push({
            label,
            value: cleanedValue,
          });
        }

        return;
      }

      if (!detailsStarted) {
        introLines.push(rawLine);
      } else {
        outroLines.push(rawLine);
      }
    });

    if (!detailsStarted || detailCount === 0) {
      return null;
    }

    if (!item.name) {
      const fallbackName = text.match(/\*\*([^*]+)\*\*/);
      if (fallbackName) {
        item.name = fallbackName[1].trim();
      }
    }

    if (!item.name) {
      return null;
    }

    return {
      introText: introLines.join("\n").trim(),
      item,
      outroText: outroLines.join("\n").trim(),
    };
  };

  // Helper function to detect if message contains structured content (lists, tables, etc.)
  const hasStructuredContent = (text) => {
    if (!text) return false;
    // Check for numbered lists (1., 2., 3., etc.)
    const numberedListPattern = /\d+\.\s+.+/;
    // Check for bullet lists (-, *, •, etc.)
    const bulletListPattern = /^[\s]*[-*•]\s+.+/m;
    // Check for multiple line breaks (indicating structured content)
    const hasMultipleLineBreaks = (text.match(/\n\n/g) || []).length >= 2;
    // Check for table-like structure (multiple colons or pipes)
    const hasTableStructure = /:.+\n/.test(text) || /\|.+/.test(text);
    
    return numberedListPattern.test(text) || bulletListPattern.test(text) || 
           (hasMultipleLineBreaks && numberedListPattern.test(text)) || hasTableStructure;
  };

  const processHeadingFormatting = (input, baseKey = 'heading') => {
    if (!input) return input;

    if (Array.isArray(input)) {
      return input.map((item, index) =>
        typeof item === 'string'
          ? processHeadingFormatting(item, `${baseKey}-${index}`)
          : item
      );
    }

    if (!input.includes('###')) {
      return input;
    }

    // Split by lines, NOT greedy regex
    const lines = input.split('\n');

    return lines.map((line, index) => {
      // #### → bold (line only)
      const h4Match = line.match(/^####\s+(.*)$/);
      if (h4Match) {
        return (
          <strong key={`${baseKey}-h4-${index}`} style={{ display: 'inline', margin: '4px 0 2px 0' }}>
            {h4Match[1]}
          </strong>
        );
      }

      // ### → h2 (line only)
      const h3Match = line.match(/^###\s+(.*)$/);
      if (h3Match) {
        return (
          <h2 key={`${baseKey}-h3-${index}`} style={{ margin: '8px 0 4px 0', fontSize: '1.1em' }}>
            {h3Match[1]}
          </h2>
        );
      }

      return line;
    });
  };

  const processImageFormatting = (input, baseKey = 'img') => {
    if (!input) return input;

    // If input is already an array, recurse into it
    if (Array.isArray(input)) {
      return input.map((item, index) =>
        typeof item === 'string'
          ? processImageFormatting(item, `${baseKey}-${index}`)
          : item
      );
    }

    if (!input.includes('![')) {
      return input;
    }

    const imageRegex = /(!\[[^\]]*\]\([^)]+\))/g;
    const parts = input.split(imageRegex);

    return parts.map((part, index) => {
      const match = part.match(/!\[([^\]]*)\]\(([^)]+)\)/);

      if (match) {
        const altText = match[1];
        const imageUrl = match[2];

        return (
          <div
            key={`${baseKey}-${index}`}
            style={{
              display: 'inline-block',
              maxWidth: '100%',
              margin: '2px 0',
              lineHeight: 0,
            }}
          >
            <img
              src={imageUrl}
              alt={altText}
              style={{
                maxWidth: '160px',
                maxHeight: '160px',
                width: 'auto',
                height: 'auto',
                objectFit: 'cover',
                margin: 0,
                borderRadius: '12px',
                display: 'block',
                verticalAlign: 'middle',
              }}
            />
          </div>
        );
      }

      return part;
    });
  };

  const processInfoFormatting = (input, baseKey = 'info') => {
    if (!input) return input;

    // Handle arrays recursively
    if (Array.isArray(input)) {
      return input.map((item, index) =>
        typeof item === 'string'
          ? processInfoFormatting(item, `${baseKey}-${index}`)
          : item
      );
    }

    // Split by lines
    const lines = input.split('\n');

    return lines.flatMap((line, index) => {
      const trimmedLine = line.trimStart();

      // Match Description
      if (trimmedLine.startsWith('Description:')) {
        return (
          <div
            key={`${baseKey}-description-${index}`}
            style={{
              margin: '2px 0',
              fontSize: '14px',
              color: '#555',
            }}
          >
            <strong>Description:</strong> {trimmedLine.replace(/^Description:\s*/, '')}
          </div>
        );
      }

      // Match Price
      if (trimmedLine.startsWith('Price:')) {
        return (
          <div
            key={`${baseKey}-price-${index}`}
            style={{
              margin: '2px 0',
              fontSize: '16px',
              fontWeight: 600,
              color: '#111',
            }}
          >
            <strong>Price:</strong> {trimmedLine.replace(/^Price:\s*/, '')}
          </div>
        );
      }

      // Match Allergens
      if (trimmedLine.startsWith('Allergens:')) {
        return (
          <div
            key={`${baseKey}-allergens-${index}`}
            style={{
              margin: '2px 0',
              fontSize: '14px',
              color: '#888',
            }}
          >
            <strong>Allergens:</strong> {trimmedLine.replace(/^Allergens:\s*/, '')}
          </div>
        );
      }

      // Otherwise return line as-is
      return line;
    });
  };


  // Helper function to process markdown images
  const processMarkdownImages = (input, baseKey = 'img') => {
    if (!input) return input;

    // Handle arrays
    if (Array.isArray(input)) {
      return input.map((item, index) =>
        typeof item === 'string'
          ? processMarkdownImages(item, `${baseKey}-${index}`)
          : item
      );
    }

    // Regex to match markdown image syntax: ![alt text](url)
    const markdownImageRegex = /!\[([^\]]*)\]\(([^)]+)\)/g;

    if (!markdownImageRegex.test(input)) {
      return input;
    }

    // Reset regex lastIndex
    markdownImageRegex.lastIndex = 0;

    const parts = [];
    let lastIndex = 0;
    let match;

    while ((match = markdownImageRegex.exec(input)) !== null) {
      const [fullMatch, altText, imageUrl] = match;
      const matchIndex = match.index;

      // Add text before the image
      if (matchIndex > lastIndex) {
        const beforeText = input.substring(lastIndex, matchIndex);
        parts.push(beforeText);
      }

      // Add the image element
      parts.push(
        <Box
          key={`${baseKey}-${matchIndex}`}
          component="img"
          src={imageUrl}
          alt={altText || 'Menu item'}
          sx={{
            maxWidth: '200px',
            width: '100%',
            height: 'auto',
            borderRadius: '8px',
            objectFit: 'cover',
            margin: '8px 0',
            display: 'block',
            cursor: 'pointer',
            transition: 'transform 0.2s',
            '&:hover': {
              transform: 'scale(1.02)',
            },
          }}
          onClick={(e) => {
            e.stopPropagation();
            // Optional: Add image viewer logic here
          }}
          onError={(e) => {
            console.error('Failed to load markdown image:', imageUrl);
            e.target.style.display = 'none';
          }}
        />
      );

      lastIndex = matchIndex + fullMatch.length;
    }

    // Add remaining text after last image
    if (lastIndex < input.length) {
      const remainingText = input.substring(lastIndex);
      parts.push(remainingText);
    }

    return parts.length > 0 ? parts : input;
  };

  // Helper function to process bold markdown formatting
  const processBoldFormatting = (input, baseKey = 'bold') => {
    if (!input) return input;

    // 🔥 FIX: handle arrays
    if (Array.isArray(input)) {
      return input.map((item, index) =>
        typeof item === 'string'
          ? processBoldFormatting(item, `${baseKey}-${index}`)
          : item
      );
    }

    if (!input.includes('**')) {
      return input;
    }

    const parts = input.split(/(\*\*[^*]+\*\*)/g);

    return parts.map((part, index) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return (
          <strong key={`${baseKey}-${index}`} style={{ display: 'inline' }}>
            {part.slice(2, -2)}
          </strong>
        );
      }
      return part;
    });
  };

  // Helper function to render text with mentions (without menu item processing)
  const renderTextWithMentions = (text) => {
    if (!text) return text;

    // Check if message already has aiMenuImages - if so, skip markdown image parsing
    // This prevents duplicate image rendering (both from aiMenuImages and markdown)
    const aiMenuImages = message?.aiMenuImages;
    const hasMenuImages = aiMenuImages && Array.isArray(aiMenuImages) && aiMenuImages.length > 0;
    
    // Remove markdown image syntax from text if aiMenuImages exists
    // This prevents showing markdown syntax as plain text when images are rendered separately
    let processedText = text;
    if (hasMenuImages) {
      const cleanMarkdownRegex = /!\[([^\]]*)\]\(([^)]+)\)/g;
      processedText = text.replace(cleanMarkdownRegex, '').trim();
      // Clean up extra whitespace and newlines
      processedText = processedText.replace(/\n\s*\n\s*\n/g, '\n\n'); // Max 2 consecutive newlines
      processedText = processedText.replace(/ {2,}/g, ' '); // Multiple spaces to single
    }
    
    // First, process markdown images to extract them (only if no aiMenuImages)
    const markdownImageRegex = /!\[([^\]]*)\]\(([^)]+)\)/g;
    const hasMarkdownImages = markdownImageRegex.test(processedText);
    markdownImageRegex.lastIndex = 0; // Reset regex

    // If text contains markdown images AND no aiMenuImages structure, process them
    if (hasMarkdownImages && !hasMenuImages) {
      const imageParts = [];
      let lastIndex = 0;
      let match;

      while ((match = markdownImageRegex.exec(processedText)) !== null) {
        const [fullMatch, altText, imageUrl] = match;
        const matchIndex = match.index;

        // Add text before the image (process mentions and bold)
        if (matchIndex > lastIndex) {
          const beforeText = processedText.substring(lastIndex, matchIndex);
          imageParts.push(processTextWithMentionsAndBold(beforeText, `before-img-${lastIndex}`));
        }

        // Add the image element
        imageParts.push(
          <Box
            key={`img-${matchIndex}`}
            component="img"
            src={imageUrl}
            alt={altText || 'Menu item'}
            sx={{
              maxWidth: '200px',
              width: '100%',
              height: 'auto',
              borderRadius: '8px',
              objectFit: 'cover',
              margin: '8px 0',
              display: 'block',
              cursor: 'pointer',
              transition: 'transform 0.2s',
              '&:hover': {
                transform: 'scale(1.02)',
              },
            }}
            onClick={(e) => {
              e.stopPropagation();
            }}
            onError={(e) => {
              console.error('Failed to load markdown image:', imageUrl);
              e.target.style.display = 'none';
            }}
          />
        );

        lastIndex = matchIndex + fullMatch.length;
      }

      // Add remaining text after last image (process mentions and bold)
      if (lastIndex < processedText.length) {
        const remainingText = processedText.substring(lastIndex);
        imageParts.push(processTextWithMentionsAndBold(remainingText, `after-img-${lastIndex}`));
      }

      return imageParts.length > 0 ? <>{imageParts}</> : processedText;
    }

    // If no markdown images (or aiMenuImages exists), process mentions and bold normally
    // If hasMenuImages is true, markdown images in text have been removed
    return processTextWithMentionsAndBold(processedText, 'text');
  };

  // Helper to process mentions and bold formatting
  const processTextWithMentionsAndBold = (text, baseKey) => {
    if (!text) return text;

    const parts = [];
    let lastIndex = 0;

    // Regex to match @username or <:username> patterns
    const mentionRegex = /(@[\w.]+|<:[\w.]+>)/g;
    let match;

    while ((match = mentionRegex.exec(text)) !== null) {
      const mention = match[0];
      const mentionStart = match.index;
      const mentionEnd = mentionStart + mention.length;

      // Add text before the mention
      if (mentionStart > lastIndex) {
        const beforeText = text.substring(lastIndex, mentionStart);
        parts.push(processBoldFormatting(beforeText, `${baseKey}-${lastIndex}`));
      }

      // Extract username from mention (remove @ or <:> wrapper)
      let username = mention;
      if (mention.startsWith("<:") && mention.endsWith(">")) {
        username = mention.substring(2, mention.length - 1);
      } else if (mention.startsWith("@")) {
        username = mention.substring(1);
      }

      // Add the highlighted mention
      parts.push(
        <Box
          key={`${baseKey}-mention-${mentionStart}`}
          component="span"
          sx={{
            color: "#7C3AED",
            fontWeight: 600,
            backgroundColor: "rgba(124, 58, 237, 0.1)",
            padding: "2px 6px",
            borderRadius: "4px",
          }}
        >
          @{username}
        </Box>
      );

      lastIndex = mentionEnd;
    }

    // Add remaining text after last mention
    if (lastIndex < text.length) {
      const remainingText = text.substring(lastIndex);
      parts.push(processBoldFormatting(remainingText, `${baseKey}-${lastIndex}`));
    }

    return parts.length > 0 ? parts : processBoldFormatting(text, `${baseKey}-only`);
  };

  // Helper function to render menu items with images
  const renderMenuItemWithImage = (itemText, itemName, images, key) => {
    return (
      <Box key={key} sx={{ mb: 3 }}>
        {/* Display images above the menu item */}
        {images && images.length > 0 && (
          <Box
            sx={{
              display: "flex",
              gap: 1,
              mb: 1.5,
              flexWrap: "wrap",
            }}
          >
            {images.map((imageName, idx) => (
              <Box
                key={idx}
                component="img"
                src={`${IMAGE_URL}${imageName}`}
                alt={itemName}
                sx={{
                  width: images.length === 1 ? "100%" : "48%",
                  maxWidth: images.length === 1 ? "200px" : "120px",
                  height: "auto",
                  borderRadius: "8px",
                  objectFit: "cover",
                  cursor: "pointer",
                  transition: "transform 0.2s",
                  "&:hover": {
                    transform: "scale(1.02)",
                  },
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  // You can add image viewer logic here if needed
                }}
                onError={(e) => {
                  console.error("Failed to load image:", `${IMAGE_URL}${imageName}`);
                  // Don't hide the image, just log the error so we can debug
                }}
              />
            ))}
          </Box>
        )}
        {/* Display menu item text */}
        <Box>{itemText}</Box>
      </Box>
    );
  };

  const renderAiMenuStructuredContent = (menuData, aiMenuImages) => {
    if (!menuData || !Array.isArray(menuData.sections) || menuData.sections.length === 0) {
      return null;
    }

    const hasMenuImages =
      aiMenuImages && Array.isArray(aiMenuImages) && aiMenuImages.length > 0;

    const resolveImageSources = (itemName, inlineImages = []) => {
      const normalizedInline = inlineImages
        .filter(Boolean)
        .map((imageSource) =>
          imageSource.startsWith("http")
            ? imageSource
            : `${IMAGE_URL}${imageSource}`
        );

      if (!hasMenuImages || !itemName) {
        return Array.from(new Set(normalizedInline));
      }

      const matchedEntry = aiMenuImages.find((entry) => {
        if (!entry?.name) {
          return false;
        }
        return entry.name.toLowerCase().includes(itemName.toLowerCase());
      });

      const resolvedAiImages = matchedEntry?.images?.map(
        (imageName) => `${IMAGE_URL}${imageName}`
      );

      return Array.from(
        new Set([...(normalizedInline || []), ...(resolvedAiImages || [])])
      );
    };

    const handleMenuItemClick = (itemName) => {
      if (!onSearchMenuItemByName || !itemName) {
        return;
      }

      onSearchMenuItemByName(itemName);
    };

    const renderIntroOrOutro = (text, position) => {
      if (!text) {
        return null;
      }

      return (
        <Box
          key={`ai-menu-${position}`}
          sx={{
            fontSize: "15px",
            lineHeight: 1.5,
          }}
        >
          {renderTextWithMentions(text)}
        </Box>
      );
    };

    const formatExtraLabel = (label) => {
      if (!label) {
        return "Note";
      }
      if (label === "note") {
        return "Note";
      }
      return label.replace(/\b\w/g, (char) => char.toUpperCase());
    };

    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {renderIntroOrOutro(menuData.introText, "intro")}

        {menuData.sections.map((section, sectionIndex) => {
          const sectionItems = Array.isArray(section.items) ? section.items : [];
          return (
            <Box
              key={`ai-menu-section-${sectionIndex}`}
              sx={{
                borderRadius: 2,
                border: "1px solid",
                borderColor: isOwn ? "rgba(255,255,255,0.25)" : "rgba(124,58,237,0.25)",
                backgroundColor: isOwn
                  ? "rgba(15, 23, 42, 0.4)"
                  : "rgba(124, 58, 237, 0.08)",
                p: 2,
                display: "flex",
                flexDirection: "column",
                gap: 1.5,
              }}
            >
            {section?.title && (
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 700,
                  color: isOwn ? "#FDE68A" : "#5B21B6",
                }}
              >
                {section.title}
              </Typography>
            )}

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "repeat(auto-fit, minmax(220px, 1fr))",
                  sm: "repeat(auto-fit, minmax(220px, 1fr))",
                },
                gap: 1.5,
              }}
            >
              {sectionItems.map((item, itemIndex) => {
                const images = resolveImageSources(item.name, item.imageUrls);
                const enableClick = Boolean(onSearchMenuItemByName);

                return (
                  <Box
                    key={`ai-menu-item-${sectionIndex}-${itemIndex}`}
                    sx={{
                      borderRadius: 1.5,
                      border: "1px solid",
                      borderColor: isOwn ? "rgba(255,255,255,0.2)" : "rgba(124,58,237,0.25)",
                      backgroundColor: isOwn ? "rgba(15, 23, 42, 0.6)" : "rgba(124, 58, 237, 0.12)",
                      p: 1.5,
                      display: "flex",
                      flexDirection: "column",
                      gap: 1,
                      boxShadow: isOwn
                        ? "0 4px 10px rgba(0,0,0,0.35)"
                        : "0 6px 18px rgba(124,58,237,0.1)",
                      minWidth: 0,
                    }}
                  >
                    {images.length > 0 && (
                      <Box
                        sx={{
                          display: "flex",
                          gap: 1,
                          flexWrap: "wrap",
                          borderRadius: 1.5,
                          overflow: "hidden",
                        }}
                      >
                        {images.map((src, imgIndex) => {
                          const imageWidth =
                            images.length === 1 ? "100%" : "calc(50% - 4px)";
                          return (
                            <Box
                              key={`ai-menu-item-image-${imgIndex}`}
                              component="img"
                              src={src}
                              alt={item.name || `menu-item-${imgIndex}`}
                              sx={{
                                width: imageWidth,
                                flex: `1 1 ${imageWidth}`,
                                maxHeight: "180px",
                                objectFit: "cover",
                                borderRadius: 1,
                              }}
                            />
                          );
                        })}
                      </Box>
                    )}

                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                      <Box
                        component="span"
                        onClick={(event) => {
                          if (!enableClick) {
                            return;
                          }
                          event.stopPropagation();
                          handleMenuItemClick(item.name);
                        }}
                        onKeyDown={(event) => {
                          if (!enableClick) {
                            return;
                          }
                          if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault();
                            event.stopPropagation();
                            handleMenuItemClick(item.name);
                          }
                        }}
                        sx={{
                          fontWeight: 700,
                          color: enableClick
                            ? isOwn
                              ? "#F9FAFB"
                              : "#7C3AED"
                            : isOwn
                              ? "#F9FAFB"
                              : "#0F172A",
                          cursor: enableClick ? "pointer" : "default",
                          textDecoration: enableClick ? "underline" : "none",
                        }}
                        role={enableClick ? "button" : undefined}
                        tabIndex={enableClick ? 0 : undefined}
                      >
                        {item.name}
                      </Box>

                      {item.description && (
                        <Typography
                          variant="body2"
                          sx={{
                            color: isOwn ? "rgba(255,255,255,0.9)" : "#4B5563",
                          }}
                        >
                          {item.description}
                        </Typography>
                      )}

                      {item.price && (
                        <Box
                          sx={{
                            fontSize: "13px",
                            fontWeight: 600,
                            color: isOwn ? "#FCD34D" : "#0F172A",
                            backgroundColor: isOwn
                              ? "rgba(253, 211, 77, 0.15)"
                              : "rgba(124, 58, 237, 0.08)",
                            px: 1,
                            py: 0.5,
                            borderRadius: 1,
                            width: "fit-content",
                          }}
                        >
                          {item.price}
                        </Box>
                      )}

                      {Array.isArray(item.extra) &&
                        item.extra.length > 0 &&
                        item.extra.map((extra, extraIndex) => (
                          <Typography
                            key={`ai-menu-extra-${extraIndex}`}
                            variant="body2"
                            sx={{
                              color: isOwn
                                ? "rgba(255,255,255,0.85)"
                                : "#6B7280",
                            }}
                          >
                            <Box
                              component="span"
                              sx={{
                                fontWeight: 600,
                                textTransform: "capitalize",
                                mr: 0.5,
                              }}
                            >
                              {formatExtraLabel(extra.label)}:
                            </Box>
                            {extra.value}
                          </Typography>
                        ))}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Box>
          );
        })}

        {renderIntroOrOutro(menuData.outroText, "outro")}
      </Box>
    );
  };

  const renderSingleMenuItemContent = (menuData, aiMenuImages) => {
    if (!menuData?.item) {
      return null;
    }

    const { item } = menuData;
    const hasMenuImages =
      aiMenuImages && Array.isArray(aiMenuImages) && aiMenuImages.length > 0;

    const resolvedImages = (() => {
      const inlineImages = (item.imageUrls || []).map((imageSource) =>
        imageSource.startsWith("http")
          ? imageSource
          : `${IMAGE_URL}${imageSource}`
      );

      if (!hasMenuImages || !item.name) {
        return Array.from(new Set(inlineImages));
      }

      const matchedEntry = aiMenuImages.find(
        (entry) =>
          entry?.name &&
          entry.name.toLowerCase().includes(item.name.toLowerCase())
      );

      const aiImages = matchedEntry?.images?.map(
        (imageName) => `${IMAGE_URL}${imageName}`
      );

      return Array.from(new Set([...(inlineImages || []), ...(aiImages || [])]));
    })();

    const renderIntroOrOutro = (text, position) => {
      if (!text) {
        return null;
      }

      return (
        <Box
          key={`single-menu-${position}`}
          sx={{
            fontSize: "15px",
            lineHeight: 1.5,
            mb: position === "intro" ? 1.5 : 0,
            mt: position === "outro" ? 1.5 : 0,
          }}
        >
          {renderTextWithMentions(text)}
        </Box>
      );
    };

    return (
      <Box sx={{ display: "flex", flexDirection: "column" }}>
        {renderIntroOrOutro(menuData.introText, "intro")}
        <Box
          sx={{
            borderRadius: 2,
            border: "1px solid",
            borderColor: isOwn ? "rgba(255,255,255,0.25)" : "rgba(124,58,237,0.25)",
            backgroundColor: isOwn
              ? "rgba(15, 23, 42, 0.4)"
              : "rgba(124, 58, 237, 0.08)",
            p: { xs: 1.5, sm: 2 },
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            gap: 2,
            alignItems: { xs: "stretch", sm: "center" },
          }}
        >
          {resolvedImages.length > 0 && (
            <Box
              sx={{
                flexShrink: 0,
                width: { xs: "100%", sm: 160 },
                display: "flex",
                justifyContent: "center",
              }}
            >
              <Box
                component="img"
                src={resolvedImages[0]}
                alt={item.name}
                sx={{
                  width: "100%",
                  height: "100%",
                  maxWidth: { xs: "100%", sm: 160 },
                  borderRadius: 2,
                  objectFit: "cover",
                  boxShadow: isOwn
                    ? "0 6px 16px rgba(0,0,0,0.35)"
                    : "0 10px 24px rgba(124,58,237,0.18)",
                }}
              />
            </Box>
          )}

          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Typography
              variant="h6"
              sx={{
                fontSize: "17px",
                fontWeight: 700,
                color: isOwn ? "#FDE68A" : "#4C1D95",
              }}
            >
              {item.name}
            </Typography>

            {item.description && (
              <Typography
                variant="body2"
                sx={{
                  color: isOwn ? "rgba(255,255,255,0.9)" : "#1F2937",
                }}
              >
                <Box component="span" sx={{ fontWeight: 600, mr: 0.5 }}>
                  Description:
                </Box>
                {item.description}
              </Typography>
            )}

            {item.price && (
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  fontSize: "13px",
                  fontWeight: 600,
                  color: isOwn ? "#FCD34D" : "#0F172A",
                  backgroundColor: isOwn
                    ? "rgba(253, 211, 77, 0.18)"
                    : "rgba(124, 58, 237, 0.12)",
                  px: 1.5,
                  py: 0.5,
                  borderRadius: 999,
                  width: "fit-content",
                }}
              >
                {item.price}
              </Box>
            )}

            {Array.isArray(item.extra) &&
              item.extra.length > 0 &&
              item.extra.map((extra, index) => (
                <Typography
                  key={`single-extra-${index}`}
                  variant="body2"
                  sx={{
                    color: isOwn ? "rgba(255,255,255,0.85)" : "#4B5563",
                  }}
                >
                  <Box component="span" sx={{ fontWeight: 600, mr: 0.5 }}>
                    {extra.label.replace(/\b\w/g, (char) => char.toUpperCase())}:
                  </Box>
                  {extra.value}
                </Typography>
              ))}
          </Box>
        </Box>
        {renderIntroOrOutro(menuData.outroText, "outro")}
      </Box>
    );
  };

  // Helper function to render message text with highlighted mentions and clickable menu items
  // Handles both @username and <:username> patterns with color highlighting
  // Also detects numbered menu items and makes them clickable
  // Preserves line breaks and formatting for structured content
  const renderMessageWithMentions = (text) => {
    if (!text) {
      return text;
    }

    // Check if this message has AI menu images data
    const aiMenuImages = message?.aiMenuImages;
    const hasMenuImages = aiMenuImages && Array.isArray(aiMenuImages) && aiMenuImages.length > 0;

    // Detect structured AI menu content via headings + bullet list
    const structuredMenu = parseAiMenuSections(text);
    if (structuredMenu) {
      return renderAiMenuStructuredContent(structuredMenu, aiMenuImages);
    }

    const singleMenuItem = parseSingleMenuItem(text);
    if (singleMenuItem) {
      return renderSingleMenuItemContent(singleMenuItem, aiMenuImages);
    }

    // Debug logging
   

    // First, check if this message contains numbered menu items (likely from AI)
    const menuItems = parseNumberedMenuItems(text);

    
    // If we have menu items with images, render them with images
    if (hasMenuImages && menuItems && menuItems.length > 0) {

      const parts = [];
      let lastIndex = 0;

      menuItems.forEach((item, idx) => {
        const { number, fullMatch, startIndex, endIndex, name: itemName } = item;
        
        // Add text before this menu item
        if (startIndex > lastIndex) {
          const beforeText = text.substring(lastIndex, startIndex);
          if (beforeText.trim()) {
            parts.push(
              <Box key={`before-${idx}`} sx={{ mb: 2 }}>
                {renderTextWithMentions(beforeText)}
              </Box>
            );
          }
        }

        // Find matching image data for this menu item
        const imageEntry = aiMenuImages.find(
          (entry) => entry.name && itemName && 
          entry.name.toLowerCase().includes(itemName.toLowerCase().split('-')[0].trim())
        );

       
        // Render menu item with or without images
        const itemText = onSearchMenuItemByName ? (
          <Box
            component="span"
            onClick={(e) => {
              e.stopPropagation();
              onSearchMenuItemByName(itemName);
            }}
            sx={{
              color: "#7C3AED",
              fontWeight: 600,
              cursor: "pointer",
              textDecoration: "underline",
              "&:hover": {
                color: "#6929C4",
              },
            }}
          >
            {renderTextWithMentions(fullMatch)}
          </Box>
        ) : (
          renderTextWithMentions(fullMatch)
        );

        parts.push(
          renderMenuItemWithImage(
            itemText,
            itemName,
            imageEntry?.images,
            `menu-${idx}`
          )
        );

        lastIndex = endIndex;
      });

      // Add remaining text after last menu item
      if (lastIndex < text.length) {
        const remainingText = text.substring(lastIndex);
        if (remainingText.trim()) {
          parts.push(
            <Box key="after-last">
              {renderTextWithMentions(remainingText)}
            </Box>
          );
        }
      }

      return <>{parts}</>;
    }
    
    // If we have menu items and the handler is available, render them as clickable (original logic)
    if (menuItems && menuItems.length > 0 && onSearchMenuItemByName) {
      const parts = [];
      let lastIndex = 0;

      // Split the text into parts, making menu items clickable
      menuItems.forEach((item, itemIndex) => {
        // Add text before the menu item
        if (item.startIndex > lastIndex) {
          const beforeText = text.substring(lastIndex, item.startIndex);
          if (beforeText) {
            // Process mentions in the text before the menu item
            const beforeParts = beforeText.split(/(@\w+|<\:\w+>)/g);
            beforeParts.forEach((part, partIndex) => {
              const atMentionMatch = part.match(/@(\w+)/);
              const bracketMentionMatch = part.match(/<:(\w+)>/);

              if (atMentionMatch || bracketMentionMatch) {
                const username = atMentionMatch
                  ? atMentionMatch[1]
                  : bracketMentionMatch[1];
                let displayName = username;

                if (message.mentions && message.mentions.length > 0) {
                  const mention = message.mentions.find(
                    (m) =>
                      m.name === username ||
                      m.userName === username ||
                      m.fullName === username
                  );
                  if (mention) {
                    displayName =
                      mention.name || mention.userName || mention.fullName || username;
                  }
                }

                parts.push(
                  <Box
                    key={`mention-${itemIndex}-${partIndex}`}
                    component="span"
                    sx={{
                      color: isOwn ? "#FFD700" : "#7C3AED",
                      fontWeight: 600,
                      backgroundColor: isOwn
                        ? "rgba(255, 215, 0, 0.1)"
                        : "rgba(124, 58, 237, 0.1)",
                      padding: "2px 4px",
                      borderRadius: "4px",
                    }}
                  >
                    @{displayName}
                  </Box>
                );
              } else if (part) {
                parts.push(part);
              }
            });
          }
        }

        // Extract just the item name part from the match (before description)
        // Find where description starts (usually after a colon or newline within the match)
        const matchText = item.fullMatch;
        const nameEndInMatch = matchText.indexOf(':') !== -1 
          ? matchText.indexOf(':')
          : matchText.indexOf('\n') !== -1
          ? matchText.indexOf('\n')
          : matchText.length;
        
        const itemNameText = matchText.substring(0, nameEndInMatch).trim();
        const afterItemName = matchText.substring(nameEndInMatch);

        // Process bold formatting in menu item name
        const processedItemName = processBoldFormatting(itemNameText, `menu-${itemIndex}`);

        // Add clickable menu item (number + name)
        parts.push(
          <Box
            key={`menu-item-${itemIndex}`}
            component="span"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onSearchMenuItemByName(item.name);
            }}
            sx={{
              color: isOwn ? "#87CEEB" : "#7C3AED",
              fontWeight: 700,
              textDecoration: "underline",
              cursor: "pointer",
              backgroundColor: isOwn
                ? "rgba(135, 206, 235, 0.15)"
                : "rgba(124, 58, 237, 0.1)",
              padding: "2px 6px",
              borderRadius: "4px",
              transition: "all 0.2s ease",
              display: "inline-block",
              "&:hover": {
                backgroundColor: isOwn
                  ? "rgba(135, 206, 235, 0.25)"
                  : "rgba(124, 58, 237, 0.2)",
                transform: "scale(1.05)",
              },
            }}
          >
            {processedItemName}
          </Box>
        );

        // Add text after the menu item name (description, ingredients, etc.)
        // Process bold formatting in the after text as well
        if (afterItemName) {
          const processedAfterText = processBoldFormatting(afterItemName, `after-menu-${itemIndex}`);
          parts.push(processedAfterText);
        }

        lastIndex = item.endIndex;
      });

      // Add remaining text after the last menu item
      if (lastIndex < text.length) {
        const remainingText = text.substring(lastIndex);
        if (remainingText) {
          const remainingParts = remainingText.split(/(@\w+|<\:\w+>)/g);
          remainingParts.forEach((part, partIndex) => {
            const atMentionMatch = part.match(/@(\w+)/);
            const bracketMentionMatch = part.match(/<:(\w+)>/);

            if (atMentionMatch || bracketMentionMatch) {
              const username = atMentionMatch
                ? atMentionMatch[1]
                : bracketMentionMatch[1];
              let displayName = username;

              if (message.mentions && message.mentions.length > 0) {
                const mention = message.mentions.find(
                  (m) =>
                    m.name === username ||
                    m.userName === username ||
                    m.fullName === username
                );
                if (mention) {
                  displayName =
                    mention.name || mention.userName || mention.fullName || username;
                }
              }

              parts.push(
                <Box
                  key={`mention-end-${partIndex}`}
                  component="span"
                  sx={{
                    color: isOwn ? "#FFD700" : "#7C3AED",
                    fontWeight: 600,
                    backgroundColor: isOwn
                      ? "rgba(255, 215, 0, 0.1)"
                      : "rgba(124, 58, 237, 0.1)",
                    padding: "2px 4px",
                    borderRadius: "4px",
                  }}
                >
                  @{displayName}
                </Box>
              );
            } else if (part) {
              parts.push(part);
            }
          });
        }
      }

      return parts;
    }

    // Original logic for mentions only (when no menu items detected)
    const parts = text.split(/(@[\p{L}\p{N}._-]+|<:[\p{L}\p{N}._-]+>)/gu);

    return parts.map((part, index) => {
      // Check if this part is a mention (@username or <:username>)
      const atMentionMatch = part.match(/^@([\p{L}\p{N}._-]+)$/u);
      const bracketMentionMatch = part.match(/^<:([\p{L}\p{N}._-]+)>$/u);

      if (atMentionMatch || bracketMentionMatch) {
        const username = atMentionMatch
          ? atMentionMatch[1]
          : bracketMentionMatch[1];

        // Try to get display name from mention data or conversation members
        let displayName = username;

        if (message.mentions && message.mentions.length > 0) {
          // Find the corresponding mention in our data
          const mention = message.mentions.find(
            (m) =>
              m.name === username ||
              m.userName === username ||
              m.fullName === username
          );

          if (mention) {
            displayName =
              mention.name || mention.userName || mention.fullName || username;
          }
        }

        // Also try to get names from conversation members if available
        if (conversationInfo?.users && displayName === username) {
          const user = conversationInfo.users.find(
            (u) => (u.fullName || u.userName || u.name) === username
          );
          if (user) {
            displayName =
              user.fullName || user.userName || user.name || username;
          }
        }

        return (
          <Box
            key={index}
            component="span"
            sx={{
              color: isOwn ? "#FFD700" : "#7C3AED", // Gold for own messages, purple for others
              fontWeight: 600,
              backgroundColor: isOwn
                ? "rgba(255, 215, 0, 0.1)"
                : "rgba(124, 58, 237, 0.1)",
              padding: "2px 4px",
              borderRadius: "4px",
            }}
          >
            @{displayName}
          </Box>
        );
      }

      // Process markdown bold formatting (**text**)
      let formatted = part;

      formatted = processHeadingFormatting(formatted, `heading-${index}`);
      formatted = processBoldFormatting(formatted, `bold-${index}`);
      formatted = processImageFormatting(formatted, `img-${index}`);
      formatted = processInfoFormatting(formatted, `info-${index}`);

      return formatted;
    });
  };


  // Create a formatted message object with mentions converted
  const formattedMessage = {
    ...message,
    text: formatMentionsForDisplay(message.text),
    originalText: message.text, // keep original for editing
  };

  // Check if timer is already expired for payment_request_upfront messages
  useEffect(() => {
    if (message?.type === "payment_request_upfront" && message?.createdAt) {
      const createdAt = new Date(message.createdAt);
      const now = new Date();
      const timeDiff = (now - createdAt) / 1000; // seconds

      if (timeDiff >= 30) {
        setTimerExpired(true);
      }
    }
  }, [message?.type, message?.createdAt]);

  // check if message is deleted for current user
  const isDeletedForMe =
    Array.isArray(message.isDeletedForMe) &&
    message.isDeletedForMe.includes(currentUserId);

  // check if message is deleted for everyone
  const isDeletedForEveryone = message.isDeletedForEveryone === true;

  // calculate read receipt status based on conversation type
  // returns icon component with appropriate color (grey for delivered, blue for read)
  const getReadReceiptStatus = () => {
    // only show read receipts for own messages
    if (!isOwn) return null;

    // FIXED: Ensure arrays are properly handled with defensive checks
    const readByLength = Array.isArray(message.readBy)
      ? message.readBy.length
      : 0;
    const deliveredToLength = Array.isArray(message.deliveredTo)
      ? message.deliveredTo.length
      : 0;

    // Get sender ID to exclude from participant count
    const senderId = message.senderId?._id || message.senderId;

    // determine if message is delivered and read
    let isDelivered = false;
    let isRead = false;

    // one-to-one chat: message is delivered when deliveredTo has at least 1 entry, read when readBy has at least 1 entry
    if (!selectedContact?.isGroup) {
      isDelivered = deliveredToLength >= 1 || message.isDelivered;
      isRead = readByLength >= 1;
    }
    // group chat: if any user in deliveredTo, show delivered (double grey tick)
    // if any user in readBy, show read (double blue tick)
    else if (selectedContact?.isGroup && conversationInfo) {
      // FIXED: For groups, if deliveredTo has any entries, message is delivered
      // Backend doesn't add sender to deliveredTo/readBy, so any entry means delivered
      isDelivered = deliveredToLength >= 1 || message.isDelivered;
      
      // For read status, calculate total participants to determine if all have read
      const { admin = [], users = [] } = conversationInfo;
      const allMembers = [...admin, ...users];
      
      // Get unique user IDs (including sender for total count)
      const uniqueUserIds = new Set();
      allMembers.forEach((member) => {
        const userId = member?.userId?._id || member?.userId;
        if (userId) {
          uniqueUserIds.add(String(userId));
        }
      });
      
      // Total users in group (including sender)
      const totalUsers = uniqueUserIds.size;
      // Expected count = total users - 1 (exclude sender)
      const expectedReadCount = totalUsers > 0 ? totalUsers - 1 : 0;
      
      // Message is read when readBy.length >= (totalUsers - 1)
      isRead = readByLength >= expectedReadCount;
    }
    // fallback: use existing logic
    else {
      isDelivered = deliveredToLength > 0 || message.isDelivered;
      isRead = message.isRead || readByLength > 0;
    }

    // return appropriate icon with color
    if (isRead) {
      // double blue tick for read
      return (
        <DoneAll
          sx={{
            fontSize: "0.9rem",
            color: "#4FC3F7", // blue color for read
          }}
        />
      );
    } else if (isDelivered) {
      // double grey tick for delivered
      return (
        <DoneAll
          sx={{
            fontSize: "0.9rem",
            color: "rgba(0,0,0,0.4)", // grey color for delivered
          }}
        />
      );
    } else {
      // single grey tick for sent
      return (
        <Done
          sx={{
            fontSize: "0.9rem",
            color: "rgba(0,0,0,0.4)", // grey color for sent
          }}
        />
      );
    }
  };

  // Helper functions for cancellation card
  const getCancelledItemCount = () => {
    if (!message?.orderId?.groupOrder?.userOrders) return 0;

    let cancelledCount = 0;
    message.orderId.groupOrder.userOrders.forEach((userOrder) => {
      userOrder.cart?.forEach((item) => {
        if (item.variantEnabled && item.variants) {
          // For variant items, count cancelled items within variants
          item.variants.forEach((variant) => {
            variant.items?.forEach((variantItem) => {
              if (variantItem.cancellation?.requested) {
                cancelledCount++;
              }
            });
          });
        } else {
          // For non-variant items, count cancelled items
          item.items?.forEach((subItem) => {
            if (subItem.cancellation?.requested) {
              cancelledCount++;
            }
          });
        }
      });
    });
    return cancelledCount;
  };

  // handle smile icon click
  const handleSmileClick = (event, side) => {
    setAnchorEl(event.currentTarget);
    setActiveSide(side);
  };

  // handle emoji picker close
  const handleClose = () => {
    setAnchorEl(null);
    setActiveSide(null);
  };

  // Timer effect for request messages - initialize once only
  useEffect(() => {
    if (
      message?.type === "request" &&
      !isOwn &&
      !hasInitialized.current &&
      !userHasResponded
    ) {
      hasInitialized.current = true;
      setTimerActive(true);
      setTimeRemaining(30);
    }
  }, [message?.type, isOwn, userHasResponded]);

  // // Timer countdown effect - simplified approach
  // useEffect(() => {
  //   if (timerActive && !userHasResponded) {
  //     let countdown = 30;

  //     const tick = () => {
  //       if (countdown > 0 && timerActive && !userHasResponded) {
  //         countdown--;
  //         setTimeRemaining(countdown);

  //         if (countdown === 0) {
  //           // Timer expired - handle automatic response only if API hasn't been called
  //           if (!apiCalled.current) {
  //             const paymentType =
  //               message?.orderId?.paymentFlow?.type ||
  //               message?.orderId?.paymentType;

  //             if (paymentType === "pay_for_all") {
  //               // Auto-accept for pay_for_all
  //               if (onPaymentResponse && message?.orderId?._id) {
  //                 apiCalled.current = true;
  //                 onPaymentResponse(message.orderId._id, "approved");
  //               }
  //             } else if (paymentType === "split_evenly") {
  //               // Auto-reject for split_evenly
  //               if (onPaymentResponse && message?.orderId?._id) {
  //                 apiCalled.current = true;
  //                 onPaymentResponse(message.orderId._id, "rejected");
  //               }
  //             }
  //           }

  //           setTimerActive(false);
  //           setUserHasResponded(true);
  //           setAutoResponseTriggered(true);
  //           return;
  //         }

  //         timerRef.current = setTimeout(tick, 1000);
  //       }
  //     };

  //     timerRef.current = setTimeout(tick, 1000);
  //   }

  //   return () => {
  //     if (timerRef.current) {
  //       clearTimeout(timerRef.current);
  //       timerRef.current = null;
  //     }
  //   };
  // }, [timerActive, userHasResponded]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  // handle emoji selection
  const handleEmojiSelect = (emoji) => {
    if (onEmojiReaction) {
      onEmojiReaction(message.id, emoji, activeSide);
    }
    handleClose();
  };

  // handle plus icon click to show extended emoji picker
  const handlePlusClick = () => {
    setShowExtendedEmoji(!showExtendedEmoji);
    handleClose(); // close the popover
  };

  // handle extended emoji selection
  const handleExtendedEmojiSelect = (emoji) => {
    if (onEmojiReaction) {
      onEmojiReaction(message.id, emoji, activeSide);
    }
    setShowExtendedEmoji(false);
    setSearchQuery("");
    setSelectedCategory("Smileys"); // Reset category to default
  };

  // handle options menu click
  const handleOptionsClick = (event) => {
    setOptionsAnchorEl(event.currentTarget);
  };

  // handle options menu close
  const handleOptionsClose = () => {
    setOptionsAnchorEl(null);
  };

  // handle edit mode functions
  const handleStartEdit = () => {
    setIsEditing(true);
    setEditText(message.text || "");
    handleOptionsClose();
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditText(message.text || "");
  };

  const handleSaveEdit = () => {
    // only save if text has changed and is not empty
    const trimmedText = editText.trim();
    if (trimmedText && trimmedText !== message.text) {
      // emit editMessage socket event with messageId and newMessage text
      if (emit && (message._id || message.id)) {
        const editPayload = {
          messageId: message._id || message.id,
          newMessage: trimmedText,
        };
        console.log("Emitting editMessage:", editPayload);
        emit("editMessage", editPayload);
      } else {
        console.error(
          "❌ Cannot edit message: missing emit function or messageId"
        );
      }
    }
    setIsEditing(false);
  };

  const handleEditKeyPress = (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      handleSaveEdit();
    } else if (event.key === "Escape") {
      event.preventDefault();
      handleCancelEdit();
    }
  };

  // Wrap onOpenPaymentDialog for pay buttons only - check wallet payment status
  // If enabled: show order details dialog, if disabled: show unavailable dialog
  const wrappedOnOpenPaymentDialog = (message) => {
    // Check order's payment settings first, fall back to prop if not available
    const orderInAppWalletPayments = message?.orderId?.paymentSettings?.enableInAppWalletPayments;
    const isInAppWalletPaymentsEnabled = orderInAppWalletPayments !== undefined 
      ? orderInAppWalletPayments 
      : enableInAppWalletPayments;
    
    console.log("wrappedOnOpenPaymentDialog called for pay button", { 
      enableInAppWalletPayments,
      orderInAppWalletPayments,
      isInAppWalletPaymentsEnabled
    });
    
    if (!isInAppWalletPaymentsEnabled) {
      console.log("Wallet payments disabled, showing unavailable dialog");
      setPendingPaymentAction(() => () => onViewOrder?.(message));
      setWalletPaymentDialogOpen(true);
    } else {
      console.log("Wallet payments enabled, showing order details dialog");
      onViewOrder?.(message);
    }
  };

  // handle wallet payment disabled dialog
  const handlePayButtonClick = (e, originalAction, message) => {
    e.stopPropagation();
    
    // Check order's payment settings first, fall back to prop if not available
    const orderInAppWalletPayments = message?.orderId?.paymentSettings?.enableInAppWalletPayments;
    const isInAppWalletPaymentsEnabled = orderInAppWalletPayments !== undefined 
      ? orderInAppWalletPayments 
      : enableInAppWalletPayments;
    
    if (!isInAppWalletPaymentsEnabled) {
      // Store the original action to execute after user confirms
      setPendingPaymentAction(() => originalAction);
      setWalletPaymentDialogOpen(true);
    } else {
      // Wallet payments enabled, proceed with original action
      originalAction(e);
    }
  };

  const handleCloseWalletPaymentDialog = () => {
    setWalletPaymentDialogOpen(false);
    setPendingPaymentAction(null);
  };

  const handleSendManualPaymentRequest = async () => {
    try {
      // Get conversationId from message (handle both string and object)
      const conversationId = 
        typeof message?.conversationId === 'string' 
          ? message.conversationId 
          : message?.conversationId?._id || message?.conversationId;
      
      if (!conversationId) {
        toast.error("conversation id not found");
        setWalletPaymentDialogOpen(false);
        return;
      }

      // Call API to send manual payment message
      await axiosInstance.post(
        ApiEndPoints.CHAT.sendManualPaymentMessage,
        {
          conversationId: conversationId,
        }
      );

      toast.success("manual payment request sent  ");
      
      // Close dialog
      setWalletPaymentDialogOpen(false);
      setPendingPaymentAction(null);
    } catch (error) {
      console.error("Error sending manual payment request:", error);
      toast.error(
        error.response?.data?.message || "failed to send manual payment request"
      );
      setWalletPaymentDialogOpen(false);
    }
  };



  // helper function to check if editing is allowed based on business chat settings
  // returns true if editing is allowed, false otherwise
  const isEditingAllowed = () => {
    // check if conversationInfo and businessLocationId exist
    if (!conversationInfo?.businessLocationId) {
      return true; // default to true if no business location (regular chat)
    }
    
    // get enableMessageEditing permission from business location settings
    const enableMessageEditing = conversationInfo.businessLocationId.enableMessageEditing;
    
    // return the permission value (defaults to true if undefined)
    return enableMessageEditing !== false;
  };

  // helper function to check if deleting is allowed based on business chat settings
  // returns true if deleting is allowed, false otherwise
  const isDeletingAllowed = () => {
    // check if conversationInfo and businessLocationId exist
    if (!conversationInfo?.businessLocationId) {
      return true; // default to true if no business location (regular chat)
    }
    
    // get enableMessageDeleting permission from business location settings
    const enableMessageDeleting = conversationInfo.businessLocationId.enableMessageDeleting;
    
    // return the permission value (defaults to true if undefined)
    return enableMessageDeleting !== false;
  };

  // handle menu options
  const handleMenuOption = (option) => {
    if (option === "forward") {
      if (onForwardMessage) {
        onForwardMessage(message);
      }
      handleOptionsClose();
      return;
    }

    if (option === "reply") {
      onReplyToMessage(message);
      handleOptionsClose();
      return;
    }

    if (option === "edit") {
      handleStartEdit();
      return;
    }

    if (option === "delete for me") {
      // emit deleteForMe socket event with messageId
      if (emit && (message._id || message.id)) {
        const messageId = message._id || message.id;

        emit("deleteForMe", { messageId });
      } else {
        console.error(
          "❌ Cannot delete message: missing emit function or messageId",
          {
            hasEmit: !!emit,
            messageId: message._id || message.id,
          }
        );
      }
      handleOptionsClose();
      return;
    }

    if (option === "delete for everyone") {
      // emit deleteForEveryone socket event with messageId
      if (emit && (message._id || message.id)) {
        const messageId = message._id || message.id;

        emit("deleteForEveryone", { messageId });
      } else {
        console.error(
          "❌ Cannot delete message for everyone: missing emit function or messageId",
          {
            hasEmit: !!emit,
            messageId: message._id || message.id,
          }
        );
      }
      handleOptionsClose();
      return;
    }

    if (option === "info") {
      // open message info dialog to show read/delivered status
      setMessageInfoDialogOpen(true);
      handleOptionsClose();
      return;
    }

    handleOptionsClose();
  };

  // open the same options popover on right-click (context menu)
  // this improves discoverability: users can right-click any message to see actions
  const handleContextMenu = (event) => {
    event.preventDefault(); // prevent browser default menu
    setOptionsAnchorEl(event.currentTarget); // anchor popover to the message bubble
  };

  // Extended emojis data
  const extendedEmojis = {
    Smileys: [
      "😀",
      "😃",
      "😄",
      "😁",
      "😆",
      "😅",
      "🤣",
      "😂",
      "🙂",
      "🙃",
      "😉",
      "😌",
      "😍",
      "🥰",
      "😘",
      "😗",
      "😙",
      "😚",
      "😋",
      "😛",
      "😝",
      "😜",
      "🤪",
      "🤨",
      "🧐",
      "🤓",
      "😎",
      "🤩",
      "🥳",
      "😏",
      "😒",
      "😞",
      "😔",
      "😟",
      "😕",
      "🙁",
      "☹️",
      "😣",
      "😖",
      "😫",
      "😩",
      "🥺",
      "😢",
      "😭",
      "😤",
      "😠",
      "😡",
      "🤬",
      "🤯",
      "😳",
      "🥵",
      "🥶",
      "😱",
      "😨",
      "😰",
      "😥",
      "😓",
      "🤗",
      "🤔",
      "🤭",
      "🤫",
      "🤥",
      "😶",
      "😐",
      "😑",
      "😯",
      "😦",
      "😧",
      "😮",
      "😲",
      "🥱",
      "😴",
      "🤤",
      "😪",
      "😵",
      "🤐",
      "🥴",
      "🤢",
      "🤮",
      "🤧",
      "😷",
      "🤒",
      "🤕",
    ],
    Gestures: [
      "👋",
      "🤚",
      "🖐️",
      "✋",
      "🖖",
      "👌",
      "🤌",
      "🤏",
      "✌️",
      "🤞",
      "🤟",
      "🤘",
      "🤙",
      "👈",
      "👉",
      "👆",
      "🖕",
      "👇",
      "☝️",
      "👍",
      "👎",
      "✊",
      "👊",
      "🤛",
      "🤜",
      "👏",
      "🙌",
      "👐",
      "🤲",
      "🤝",
      "🙏",
      "✍️",
      "💪",
      "🦾",
      "🦿",
      "🦵",
      "🦶",
      "👂",
      "🦻",
      "👃",
      "🧠",
      "🫀",
      "🫁",
      "🦷",
      "🦴",
      "👀",
      "👁️",
      "👅",
      "👄",
      "💋",
      "🩸",
    ],
    Animals: [
      "🐶",
      "🐱",
      "🐭",
      "🐹",
      "🐰",
      "🦊",
      "🐻",
      "🐼",
      "🐨",
      "🐯",
      "🦁",
      "🐮",
      "🐷",
      "🐸",
      "🐵",
      "🐔",
      "🐧",
      "🐦",
      "🐤",
      "🐣",
      "🦆",
      "🦅",
      "🦉",
      "🦇",
      "🐺",
      "🐗",
      "🐴",
      "🦄",
      "🐝",
      "🐛",
      "🦋",
      "🐌",
      "🐞",
      "🐜",
      "🦟",
      "🦗",
      "🕷️",
      "🕸️",
      "🦂",
      "🐢",
      "🐍",
      "🦎",
      "🦖",
      "🦕",
      "🐙",
      "🦑",
      "🦐",
      "🦞",
      "🦀",
      "🐡",
      "🐠",
      "🐟",
      "🐬",
      "🐳",
      "🐋",
      "🦈",
      "🐊",
      "🐅",
      "🐆",
      "🦓",
      "🦍",
      "🦧",
      "🐘",
      "🦛",
      "🦏",
      "🐪",
      "🐫",
      "🦙",
      "🦒",
      "🐃",
      "🐂",
      "🐄",
      "🐎",
      "🐖",
      "🐏",
      "🐑",
      "🐐",
      "🦌",
      "🐕",
      "🐩",
      "🦮",
      "🐕‍🦺",
      "🐈",
      "🐈‍⬛",
      "🐓",
      "🦃",
      "🦚",
      "🦜",
      "🦢",
      "🦩",
      "🕊️",
      "🐇",
      "🦝",
      "🦨",
      "🦡",
      "🦫",
      "🦦",
      "🦥",
      "🐁",
      "🐀",
      "🐿️",
      "🦔",
    ],
    Food: [
      "🍎",
      "🍐",
      "🍊",
      "🍋",
      "🍌",
      "🍉",
      "🍇",
      "🍓",
      "🫐",
      "🍈",
      "🍒",
      "🍑",
      "🥭",
      "🍍",
      "🥥",
      "🥝",
      "🍅",
      "🍆",
      "🥑",
      "🥦",
      "🥬",
      "🥒",
      "🌶️",
      "🫑",
      "🌽",
      "🥕",
      "🫒",
      "🧄",
      "🧅",
      "🥔",
      "🍠",
      "🥐",
      "🥯",
      "🍞",
      "🥖",
      "🥨",
      "🧀",
      "🥚",
      "🍳",
      "🧈",
      "🥞",
      "🧇",
      "🥓",
      "🥩",
      "🍗",
      "🍖",
      "🦴",
      "🌭",
      "🍔",
      "🍟",
      "🍕",
      "🥪",
      "🥙",
      "🧆",
      "🌮",
      "🌯",
      "🫔",
      "🥗",
      "🥘",
      "🫕",
      "🥫",
      "🍝",
      "🍜",
      "🍲",
      "🍛",
      "🍣",
      "🍱",
      "🥟",
      "🦪",
      "🍤",
      "🍙",
      "🍚",
      "🍘",
      "🍥",
      "🥠",
      "🥮",
      "🍢",
      "🍡",
      "🍧",
      "🍨",
      "🍦",
      "🥧",
      "🧁",
      "🍰",
      "🎂",
      "🍮",
      "🍭",
      "🍬",
      "🍫",
      "🍿",
      "🍩",
      "🍪",
      "🌰",
      "🥜",
      "🍯",
      "🥛",
      "🍼",
      "🫖",
      "☕",
      "🍵",
      "🧃",
      "🥤",
      "🧋",
      "🍶",
      "🍺",
      "🍷",
      "🥂",
      "🥃",
      "🍸",
      "🍹",
      "🧉",
      "🍾",
      "🧊",
      "🥄",
      "🍴",
      "🍽️",
      "🥡",
      "🥢",
      "🧂",
      "🥣",
      "🥡",
      "🥄",
      "🔪",
      "🍽️",
      "🏺",
    ],
    Activities: [
      "⚽",
      "🏀",
      "🏈",
      "⚾",
      "🥎",
      "🎾",
      "🏐",
      "🏉",
      "🥏",
      "🎱",
      "🪀",
      "🏓",
      "🏸",
      "🏒",
      "🏑",
      "🥍",
      "🏏",
      "🥅",
      "⛳",
      "🪁",
      "🏹",
      "🎣",
      "🤿",
      "🥊",
      "🥋",
      "🎽",
      "🛹",
      "🛷️",
      "⛸️",
      "🥌",
      "🎿",
      "⛷️",
      "🏂",
      "🪂",
      "🏋️‍♀️",
      "🏋️",
      "🏋️‍♂️",
      "🤼‍♀️",
      "🤼",
      "🤼‍♂️",
      "🤸‍♀️",
      "🤸",
      "🤸‍♂️",
      "⛹️‍♀️",
      "⛹️",
      "⛹️‍♂️",
      "🤺",
      "🤾‍♀️",
      "🤾",
      "🤾‍♂️",
      "🏊‍♀️",
      "🏊",
      "🏊‍♂️",
      "🤽‍♀️",
      "🤽",
      "🤽‍♂️",
      "🚣‍♀️",
      "🚣",
      "🚣‍♂️",
      "🧗‍♀️",
      "🧗",
      "🧗‍♂️",
      "🚵‍♀️",
      "🚵",
      "🚵‍♂️",
      "🚴‍♀️",
      "🚴",
      "🚴‍♂️",
      "🏆",
      "🥇",
      "🥈",
      "🥉",
      "🏅",
      "🎖️",
      "🏵️",
      "🎗️",
      "🎟️",
      "🎫",
      "🎭",
      "🎨",
      "🎬",
      "🎤",
      "🎧",
      "🎼",
      "🎹",
      "🥁",
      "🪘",
      "🎷",
      "🎺",
      "🎸",
      "🪕",
      "🎻",
      "🎲",
      "♟️",
      "🎯",
      "🎳",
      "🎮",
      "🎰",
      "🧩",
      "🎨",
      "📱",
      "💻",
      "⌨️",
      "🖥️",
      "🖨️",
      "🖱️",
      "🖲️",
      "🕹️",
      "🎮",
      "🎲",
      "♟️",
      "🎯",
      "🎳",
      "🎰",
      "🧩",
      "🎨",
      "📱",
      "💻",
      "⌨️",
      "🖥️",
      "🖨️",
      "🖱️",
      "🖲️",
      "🕹️",
    ],
  };

  const getFilteredExtendedEmojis = () => {
    const categoryEmojis = extendedEmojis[selectedCategory];
    if (!categoryEmojis) return [];

    if (!searchQuery) return categoryEmojis;

    // Search across all categories if there's a search query
    const allEmojis = Object.values(extendedEmojis).flat();
    return allEmojis.filter(
      (emoji) =>
        emoji.includes(searchQuery) ||
        emoji.toLowerCase().includes(searchQuery.toLowerCase())
    );
  };

  // predefined emojis
  const emojis = ["😊", "❤️", "👍", "😂", "🎉"];

  // check if emoji picker is open
  const open = Boolean(anchorEl);

  // helper function to render media grid
  const renderMediaGrid = (attachments) => {
    if (!attachments || attachments.length === 0) return null;

    const displayCount = Math.min(attachments.length, 4);
    const hasMore = attachments.length > 4;

    return (
      <MediaGrid
        count={displayCount}
        onClick={() => onMediaClick && onMediaClick({ attachments })}
      >
        {attachments.slice(0, displayCount).map((media, index) => (
          <MediaItem key={index}>
            {media.type === "image" ? (
              <MediaImage
                src={ media.url}
                alt={media.fileName || "image"}
                loading="lazy"
              />
            ) : media.type === "video" ? (
              <MediaVideo
                src={ media.url}
                poster={ media.thumbnail}
                muted
                preload="metadata"
              />
            ) : media.type === "voice" || media.type === "audio" ? (
              <Box
                sx={{
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: isOwn
                    ? "rgba(255, 255, 255, 0.1)"
                    : "rgba(124, 58, 237, 0.1)",
                  borderRadius: 1,
                  p: 2,
                  gap: 1,
                }}
              >
                <AudioIcon
                  sx={{
                    fontSize: 32,
                    color: isOwn ? "rgba(255, 255, 255, 0.9)" : "#7C3AED",
                  }}
                />
                <audio
                  controls
                  src={getImageDisplayUrl(media.url, AUDIO_URL)}
                  style={{
                    width: "100%",
                    maxWidth: "200px",
                  }}
                />
                {media.duration && media.duration !== "0" && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: isOwn
                        ? "rgba(255, 255, 255, 0.7)"
                        : "rgba(0, 0, 0, 0.6)",
                      fontSize: "0.7rem",
                    }}
                  >
                    {formatDuration(media.duration)}
                  </Typography>
                )}
              </Box>
            ) : media.type === "document" ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  p: 1.5,
                  gap: 1.5,
                  width: "100%",
                  bgcolor: isOwn
                    ? "#7C3AED"
                    : "rgba(124, 58, 237, 0.08)",
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: isOwn
                    ? "rgba(255, 255, 255, 0.3)"
                    : "rgba(124, 58, 237, 0.15)",
                  minWidth: "220px",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    bgcolor: isOwn
                      ? "rgba(255, 255, 255, 0.2)"
                      : "rgba(124, 58, 237, 0.12)",
                  }
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 40,
                    height: 40,
                    borderRadius: 1.5,
                    bgcolor: isOwn ? "rgba(255, 255, 255, 0.2)" : "#7C3AED",
                    color: "white",
                  }}
                >
                  <FileIcon sx={{ fontSize: 24 }} />
                </Box>
                <Box sx={{ overflow: "hidden", flex: 1 }}>
                  <Typography
                    variant="body2"
                    noWrap
                    sx={{
                      color: isOwn ? "#ffffff" : "#1F2937",
                      display: "block",
                      fontWeight: 600,
                      fontSize: "0.9rem",
                    }}
                  >
                    {media.fileName || "Document"}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: isOwn ? "rgba(255, 255, 255, 0.8)" : "#6B7280",
                      fontSize: "0.75rem",
                    }}
                  >
                    {formatFileSize(media.size)}
                  </Typography>
                </Box>
                <IconButton
                  size="small"
                  component="a"
                  href={media.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  download={media.fileName}
                  onClick={(e) => e.stopPropagation()}
                  sx={{
                    color: isOwn ? "#ffffff" : "#7C3AED",
                    bgcolor: isOwn
                      ? "rgba(255, 255, 255, 0.1)"
                      : "rgba(124, 58, 237, 0.05)",
                    "&:hover": {
                      bgcolor: isOwn
                        ? "rgba(255, 255, 255, 0.2)"
                        : "rgba(124, 58, 237, 0.1)",
                    },
                  }}
                >
                  <DownloadIcon fontSize="small" />
                </IconButton>
              </Box>
            ) : media.type === "contact" ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  p: 1.5,
                  width: "100%",
                  bgcolor: isOwn
                    ? "rgba(255, 255, 255, 0.1)"
                    : "rgba(124, 58, 237, 0.08)",
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: isOwn
                    ? "rgba(255, 255, 255, 0.2)"
                    : "rgba(124, 58, 237, 0.15)",
                  minWidth: "180px",
                }}
              >
                <PersonIcon
                  sx={{
                    fontSize: 28,
                    color: isOwn ? "rgba(255,255,255,0.9)" : "#7C3AED",
                  }}
                />
                <Box sx={{ overflow: "hidden" }}>
                  <Typography
                    variant="body2"
                    noWrap
                    sx={{
                      fontWeight: 600,
                      color: isOwn ? "#fff" : "#1F2937",
                      fontSize: "0.9rem",
                    }}
                  >
                    {media.displayName || media.name || media.contactName || "Contact"}
                  </Typography>
                  {(media.phoneNumber || media.phone) && (
                    <Typography
                      variant="caption"
                      sx={{
                        color: isOwn ? "rgba(255,255,255,0.8)" : "#6B7280",
                        fontSize: "0.75rem",
                      }}
                    >
                      {media.phoneNumber || media.phone}
                    </Typography>
                  )}
                </Box>
              </Box>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  p: 1.5,
                  gap: 1.5,
                  width: "100%",
                  bgcolor: isOwn
                    ? "rgba(255, 255, 255, 0.1)"
                    : "rgba(124, 58, 237, 0.08)",
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: isOwn
                    ? "rgba(255, 255, 255, 0.2)"
                    : "rgba(124, 58, 237, 0.15)",
                  minWidth: "180px",
                }}
              >
                <FileIcon sx={{ fontSize: 24, color: isOwn ? "#fff" : "#7C3AED" }} />
                <Typography
                  variant="body2"
                  noWrap
                  sx={{
                    color: isOwn ? "#ffffff" : "#1F2937",
                    fontSize: "0.9rem",
                  }}
                >
                  {media.fileName || "media"}
                </Typography>
              </Box>
            )}

            {/* show +X overlay for the last item when there are more than 4 */}
            {index === 3 && hasMore && (
              <MediaOverlay>+{attachments.length - 4}</MediaOverlay>
            )}
          </MediaItem>
        ))}

        {/* show total count badge */}
        {hasMore && <MediaCounter>{attachments.length} media</MediaCounter>}
      </MediaGrid>
    );
  };

  // Helper function to format duration (seconds to MM:SS)
  const formatDuration = (duration) => {
    if (!duration || duration === "0") return "";
    const seconds = parseInt(duration, 10);
    if (isNaN(seconds)) return "";
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
   return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  // Get formatted message text with mentions converted to <:username> format
  const getFormattedMessageText = () => {
    return formatMentionsForDisplay(message.text);
  };

  // Typing dots loader component for AI streaming
  const TypingDotsLoader = ({ isOwn }) => {
    const animationName = `typing-dot-ai-loader`;
    
    return (
      <>
        <style>
          {`
            @keyframes ${animationName} {
              0%, 60%, 100% {
                transform: translateY(0);
                opacity: 0.5;
              }
              30% {
                transform: translateY(-8px);
                opacity: 1;
              }
            }
          `}
        </style>
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.75,
            padding: "8px 0",
            minHeight: "20px",
            width: "100%",
          }}
        >
          {[0, 1, 2].map((index) => (
            <Box
              key={index}
              component="span"
              sx={{
                width: 10,
                height: 10,
                borderRadius: "50%",
                backgroundColor: isOwn
                  ? "rgba(255, 255, 255, 0.9)"
                  : "rgba(124, 58, 237, 0.6)",
                animation: `${animationName} 1.4s infinite ease-in-out`,
                animationDelay: `${index * 0.2}s`,
                display: "inline-block",
              }}
            />
          ))}
        </Box>
      </>
    );
  };

  // Helper function to generate personalized message text based on payment flow and user role
  const generatePaymentMessageText = () => {
    const initiatedBy = message?.orderId?.paymentFlow?.initiatedBy;
    const responses = message?.orderId?.paymentFlow?.request?.responses || [];
    const paymentFlowStatus = message?.orderId?.paymentFlow?.status;
    const paymentType = message?.orderId?.paymentType;
    const initiatorName =
      message?.orderId?.paymentFlow?.initiatorName || "Someone";
    const orderNumber = message?.orderId?.orderNumber || "N/A";
    const totalAmount = message?.orderId?.totalAmount || 0;
    const userOrder = message?.orderId?.groupOrder?.userOrders?.find(
      (order) => {
        const userId = order.userId?._id || order.userId;
        return userId === currentUserId;
      }
    );
    const userAmountToPay = userOrder?.amountToPay || 0;
    const userAmountPaid = userOrder?.amountPaid || 0;
    const userRemainingAmount = userOrder?.remainingAmount || 0;
    const upfrontPercentage =
      message?.orderId?.paymentSettings?.upfrontPercentage || 0;
    const currencyCode = message?.orderId?.currency;
    const currencySymbol = getCurrencySymbol(currencyCode);

    const isInitiator = initiatedBy === currentUserId;
    const currentUserResponse = responses.find(
      (response) => response.userId === currentUserId
    );
    const hasUserPaidOwnAmount =
      userAmountPaid > 0 && userOrder?.paymentStatus === "upfront_paid";

    // System Order Messages (Always shown to all)
    if (message?.type === "order") {
      const orderStatus = message?.orderId?.status;
      if (orderStatus === "created") {
        return `Order #${orderNumber} has been placed by ${
          message.sender || "Someone"
        }.`;
      } else if (orderStatus === "updated") {
        return `Order #${orderNumber} was updated.`;
      }
      return formattedMessage.text || "Order message";
    }

    // Payment Flow Messages
    if (message?.type === "payment-request") {
      // Pay for All Messages
      if (paymentType === "pay_for_all") {
        if (paymentFlowStatus === "pending_approval") {
          if (isInitiator) {
            return "You initiated Pay for All. Waiting for others to approve.";
          } else {
            return `${initiatorName} wants to pay for everyone's bill. Do you accept?`;
          }
        } else if (paymentFlowStatus === "approved") {
          if (isInitiator) {
            return `Everyone approved. Please pay total ${currencySymbol}${totalAmount.toFixed(
              2
            )}.`;
          } else if (hasUserPaidOwnAmount) {
            return `You accepted ${initiatorName}'s Pay for All request. You don't need to pay.`;
          } else {
            return `You accepted ${initiatorName}'s Pay for All request. You don't need to pay.`;
          }
        } else if (paymentFlowStatus === "rejected") {
          const rejectedUsers = responses
            .filter((response) => response.response === "rejected")
            .map((response) => response.userName || "Someone");

          if (currentUserResponse?.response === "rejected") {
            return "You rejected. You will pay your own bill.";
          } else if (isInitiator) {
            const rejectedNames = rejectedUsers.join(", ");
            return `${rejectedNames} rejected. ${rejectedNames} will pay separately.`;
          } else {
            const rejectedNames = rejectedUsers.join(", ");
            return `${rejectedNames} rejected ${initiatorName}'s Pay for All request.`;
          }
        }
      }

      // Split Evenly Messages
      if (paymentType === "split_evenly") {
        if (paymentFlowStatus === "pending_approval") {
          if (isInitiator) {
            return "You initiated Split Evenly. Waiting for others to approve.";
          } else {
            return `${initiatorName} wants to split the bill evenly. Do you accept?`;
          }
        } else if (paymentFlowStatus === "approved") {
          if (currentUserResponse?.response === "approved") {
            return `You accepted. Your share is ${currencySymbol}${userAmountToPay.toFixed(
              2
            )}.`;
          }
        } else if (paymentFlowStatus === "rejected") {
          if (currentUserResponse?.response === "rejected") {
            return `You rejected. You will pay your own bill (${currencySymbol}${userAmountToPay.toFixed(
              2
            )}).`;
          } else if (isInitiator) {
            const rejectedUsers = responses
              .filter((response) => response.response === "rejected")
              .map((response) => response.userName || "Someone");
            const approvedUsers = responses
              .filter((response) => response.response === "approved")
              .map((response) => response.userName || "Someone");
            const remainingUsers =
              approvedUsers.length > 0 ? approvedUsers.join(" & ") : "others";
            return `${rejectedUsers.join(
              ", "
            )} rejected Split Evenly. Remaining amount split among ${remainingUsers}.`;
          }
        }
      }

      // Split Per Person Messages
      if (paymentType === "split_per_person") {
        if (
          paymentFlowStatus === "pending_approval" ||
          paymentFlowStatus === "approved"
        ) {
          if (userAmountPaid > 0) {
            const remainingUpfrontAmount =
              upfrontPercentage > 0
                ? (userRemainingAmount * upfrontPercentage) / 100
                : userRemainingAmount;
            return `Your bill is ${currencySymbol}${userAmountToPay.toFixed(
              2
            )}. Please pay (Upfront ${currencySymbol}${userAmountPaid.toFixed(
              2
            )} already paid, remaining: ${currencySymbol}${remainingUpfrontAmount.toFixed(
              2
            )}).`;
          } else {
            return `Your bill is ${currencySymbol}${userAmountToPay.toFixed(2)}. Please pay.`;
          }
        }
      }

      // Flow Reset Messages
      if (paymentFlowStatus === "reset") {
        return `${initiatorName} has reset the payment flow. A new payment option can be chosen.`;
      }

      // Auto-response messages
      const noResponseUsers = responses
        .filter(
          (response) =>
            response.response === "auto_approved" ||
            response.response === "auto_rejected"
        )
        .map((response) => response.userName || "Someone");

      if (noResponseUsers.length > 0 && paymentType === "pay_for_all") {
        return `No response from ${noResponseUsers.join(
          ", "
        )}. Auto-approved Pay for All.`;
      } else if (noResponseUsers.length > 0 && paymentType === "split_evenly") {
        return `No response from ${noResponseUsers.join(
          ", "
        )}. Auto-rejected Split Evenly.`;
      }
    }

    // Final Payment Completion Messages
    if (message?.type === "payment-completion") {
      const paidBy = message?.orderId?.paymentFlow?.paidBy;
      const paidByName = message?.orderId?.paymentFlow?.paidByName || "Someone";

      if (paymentType === "pay_for_all" && paidBy === initiatedBy) {
        return `${paidByName} paid ${currencySymbol}${totalAmount.toFixed(
          2
        )}. Order #${orderNumber} is fully paid.`;
      } else if (paymentType === "split_evenly") {
        if (isInitiator) {
          return (
            "All users paid their share. Order #" +
            orderNumber +
            " is fully paid."
          );
        }
      } else if (paymentType === "split_per_person") {
        if (paidBy === currentUserId) {
          return "Payment completed for your bill.";
        } else {
          return `${paidByName} paid their bill (${currencySymbol}${userAmountToPay.toFixed(
            2
          )}).`;
        }
      }
    }

    // Upfront Payment Messages
    if (message?.type === "upfront-payment") {
      if (userAmountPaid > 0) {
        return `You paid upfront ${currencySymbol}${userAmountPaid.toFixed(
          2
        )}. Remaining ${currencySymbol}${userRemainingAmount.toFixed(
          2
        )} will be charged later.`;
      } else {
        const upfrontAmount = (userAmountToPay * upfrontPercentage) / 100;
        return `You must pay ${upfrontPercentage}% upfront (${currencySymbol}${upfrontAmount.toFixed(
          2
        )}) before confirming your order.`;
      }
    }

    // Handle payment request when user's paymentType is null
    if (
      message?.type === "payment_request" &&
      userOrder?.paymentType === null
    ) {
      // Check if message contains "wants to treat" pattern
      const messageText = formattedMessage.text || "";
      const treatPattern = /^(\w+)\s+wants to treat/;
      const treatMatch = messageText.match(treatPattern);

      if (treatMatch) {
        const initiatorName = treatMatch[1];
        return `${initiatorName} initiated payment for the order`;
      }

      // Find the user who initiated payment (has paymentType "pay_for_all")
      const initiatorOrder = message?.orderId?.groupOrder?.userOrders?.find(
        (order) => order.paymentType === "pay_for_all"
      );
      const initiatorName = initiatorOrder?.userFullName || "Someone";

      return `${initiatorName} initiated payment for the order`;
    }

    // Handle payment request when user already paid upfront
    if (
      message?.type === "payment_request" &&
      userOrder?.paymentStatus === "upfront_paid"
    ) {
      // Check if message contains "wants to treat" pattern
      const messageText = formattedMessage.text || "";
      const treatPattern = /^(\w+)\s+wants to treat/;
      const treatMatch = messageText.match(treatPattern);

      if (treatMatch) {
        const initiatorName = treatMatch[1];
        return `${initiatorName} wants to pay your upfront payment`;
      }

      // Find the user who initiated payment (has paymentType "pay_for_all")
      const initiatorOrder = message?.orderId?.groupOrder?.userOrders?.find(
        (order) => order.paymentType === "pay_for_all"
      );
      const initiatorName = initiatorOrder?.userFullName || "Someone";

      return `${initiatorName} wants to pay your upfront payment`;
    }

    // Default fallback
    return formattedMessage.text || "Payment message";
  };

  // Debug: Log message type and user info

  // Special handling for add-remove messages - show as centered system message
  if (message?.type === "add-remove") {
    return (
      <AddRemoveMessage
        message={message}
        currentUser={currentUser}
        storedUserId={storedUserId}
        onOpenNotes={onOpenNotes}
      />
    );
  }

  // Special handling for payment_response messages - hide from users with split_per_person
  if (message?.type === "payment_response") {
    return (
      <PaymentResponseMessage
        message={message}
        currentUser={currentUser}
        storedUserId={storedUserId}
        isCurrentUserOwnerOrTeamMember={isCurrentUserOwnerOrTeamMember}
      />
    );
  }

  // Special handling for payment_request_split_evenly messages
  if (message?.type === "payment_request_split_evenly") {
    // Hide payment_request_split_evenly messages from business owners and team members
    // These messages are only for customers (regular users)
    if (isCurrentUserOwnerOrTeamMember) {
      return null; // Don't render payment_request_split_evenly messages for owners/team members
    }
    
    return (
      <PaymentRequestSplitEvenly
        message={message}
        isOwn={isOwn}
        currentUser={currentUser}
        storedUserId={storedUserId}
      />
    );
  }

  // Special handling for payment_bill_split_evenly messages - show pay button for split evenly payment
  if (message?.type === "payment_bill_split_evenly") {
    return (
      <PaymentBillSplitEvenlyMessage
        message={message}
        isOwn={isOwn}
        currentUser={currentUser}
        storedUserId={storedUserId}
        isCurrentUserOwnerOrTeamMember={isCurrentUserOwnerOrTeamMember}
        onViewOrder={onViewOrder}
        onOpenPaymentDialog={onOpenPaymentDialog}
        getReadReceiptStatus={getReadReceiptStatus}
        getCurrencySymbol={getCurrencySymbol}
        formattedMessage={formattedMessage}
        wrappedOnOpenPaymentDialog={wrappedOnOpenPaymentDialog}
        theme={theme}
      />
    );
  }

  // Special handling for payment_request_bill messages - show bill with tip here button
  if (message?.type === "payment_request_bill") {
    return (
      <PaymentRequestBillMessage
        message={message}
        isOwn={isOwn}
        currentUser={currentUser}
        storedUserId={storedUserId}
        onOpenPaymentDialog={onOpenPaymentDialog}
        getReadReceiptStatus={getReadReceiptStatus}
        formattedMessage={formattedMessage}
        isCurrentUserOwnerOrTeamMember={isCurrentUserOwnerOrTeamMember}
        conversationInfo={conversationInfo}
        wrappedOnOpenPaymentDialog={wrappedOnOpenPaymentDialog}
      />
    );
  }

  // Special handling for payment_request_upfront messages - show appropriate message to each user
  if (message?.type === "payment_request_upfront") {
    return (
      <PaymentRequestUpfrontMessage
        message={message}
        isOwn={isOwn}
        currentUser={currentUser}
        storedUserId={storedUserId}
        isCurrentUserOwnerOrTeamMember={isCurrentUserOwnerOrTeamMember}
        onOpenPaymentDialog={onOpenPaymentDialog}
        getReadReceiptStatus={getReadReceiptStatus}
        formattedMessage={formattedMessage}
      />
    );
  }

  // Special handling for payment_request_pay_for_all messages
  if (message?.type === "payment_request_pay_for_all") {
    return (
      <PaymentRequestPayForAllMessage
        message={message}
        isOwn={isOwn}
        currentUser={currentUser}
        storedUserId={storedUserId}
        isCurrentUserOwnerOrTeamMember={isCurrentUserOwnerOrTeamMember}
        onOpenPaymentDialog={onOpenPaymentDialog}
        getReadReceiptStatus={getReadReceiptStatus}
        getCurrencySymbol={getCurrencySymbol}
        formattedMessage={formattedMessage}
        wrappedOnOpenPaymentDialog={wrappedOnOpenPaymentDialog}
        walletPaymentDialogOpen={walletPaymentDialogOpen}
        handleCloseWalletPaymentDialog={handleCloseWalletPaymentDialog}
        handleSendManualPaymentRequest={handleSendManualPaymentRequest}
      />
    );
  }

  // Special handling for payment_request_split_per_person messages
  if (message?.type === "payment_request_split_per_person") {
    return (
      <PaymentRequestSplitPerPersonMessage
        message={message}
        isOwn={isOwn}
        currentUser={currentUser}
        storedUserId={storedUserId}
        isCurrentUserOwnerOrTeamMember={isCurrentUserOwnerOrTeamMember}
        onOpenPaymentDialog={onOpenPaymentDialog}
        getReadReceiptStatus={getReadReceiptStatus}
        getCurrencySymbol={getCurrencySymbol}
        formattedMessage={formattedMessage}
        wrappedOnOpenPaymentDialog={wrappedOnOpenPaymentDialog}
        walletPaymentDialogOpen={walletPaymentDialogOpen}
        handleCloseWalletPaymentDialog={handleCloseWalletPaymentDialog}
        handleSendManualPaymentRequest={handleSendManualPaymentRequest}
      />
    );
  }

  // Special handling for payment_retry_option messages - show only to initiator
  if (message?.type === "payment_retry_option") {
    return (
      <PaymentRetryOptionMessage
        message={message}
        isOwn={isOwn}
        currentUser={currentUser}
        storedUserId={storedUserId}
        getReadReceiptStatus={getReadReceiptStatus}
        getFormattedMessageText={getFormattedMessageText}
      />
    );
  }

  // Special handling for payment_request_individual messages - show pay button for individual payment
  if (message?.type === "payment_request_individual") {
    return (
      <PaymentRequestIndividualMessage
        message={message}
        isOwn={isOwn}
        currentUser={currentUser}
        storedUserId={storedUserId}
        onViewOrder={onViewOrder}
        onOpenPaymentDialog={onOpenPaymentDialog}
        getReadReceiptStatus={getReadReceiptStatus}
        getCurrencySymbol={getCurrencySymbol}
        formattedMessage={formattedMessage}
        wrappedOnOpenPaymentDialog={wrappedOnOpenPaymentDialog}
        walletPaymentDialogOpen={walletPaymentDialogOpen}
        handleCloseWalletPaymentDialog={handleCloseWalletPaymentDialog}
        handleSendManualPaymentRequest={handleSendManualPaymentRequest}
      />
    );
  }

  // Special handling for payment_request_individual_remaining_amount messages - show pay button for remaining amount
  if (message?.type === "payment_request_individual_remaining_amount") {
    return (
      <PaymentRequestIndividualRemainingAmountMessage
        message={message}
        isOwn={isOwn}
        currentUser={currentUser}
        storedUserId={storedUserId}
        onOpenPaymentDialog={onOpenPaymentDialog}
        getReadReceiptStatus={getReadReceiptStatus}
        getCurrencySymbol={getCurrencySymbol}
        formattedMessage={formattedMessage}
        wrappedOnOpenPaymentDialog={wrappedOnOpenPaymentDialog}
        walletPaymentDialogOpen={walletPaymentDialogOpen}
        handleCloseWalletPaymentDialog={handleCloseWalletPaymentDialog}
        handleSendManualPaymentRequest={handleSendManualPaymentRequest}
      />
    );
  }

  // Special handling for payment_option messages - show with tip here button
  if (message?.type === "payment_option") {
    return (
      <PaymentOptionMessage
        message={message}
        isOwn={isOwn}
        currentUser={currentUser}
        storedUserId={storedUserId}
        isCurrentUserOwnerOrTeamMember={isCurrentUserOwnerOrTeamMember}
        onOpenPaymentDialog={onOpenPaymentDialog}
        getReadReceiptStatus={getReadReceiptStatus}
        formattedMessage={formattedMessage}
        getFormattedMessageText={getFormattedMessageText}
      />
    );
  }

  // Hide payment_request messages from business owners and team members
  if (message?.type === "payment_request" && isCurrentUserOwnerOrTeamMember) {
    return null; // Don't render payment_request messages for owners/team members
  }

  // Filter payment_request messages based on message content
  if (message?.type === "payment_request") {
    return (
      <PaymentRequestMessage
        message={message}
        isOwn={isOwn}
        currentUser={currentUser}
        storedUserId={storedUserId}
        formattedMessage={formattedMessage}
        getFormattedMessageText={getFormattedMessageText}
        getCurrencySymbol={getCurrencySymbol}
        wrappedOnOpenPaymentDialog={wrappedOnOpenPaymentDialog}
        getReadReceiptStatus={getReadReceiptStatus}
        generatePaymentMessageText={generatePaymentMessageText}
        theme={theme}
      />
    );
  }

  // Show payment-request messages based on payment type and status
  if (message?.type === "payment-request") {
    const initiatedBy = message?.orderId?.paymentFlow?.initiatedBy;
    const responses = message?.orderId?.paymentFlow?.request?.responses || [];
    const paymentFlowStatus = message?.orderId?.paymentFlow?.status;
    const paymentType = message?.orderId?.paymentType;

    // Show message to:
    // 1. The initiator (always sees the message)
    // 2. Users who rejected the request (when status is "rejected")
    // 3. Users who approved the request (for split payments only)
    // 4. ALL users for approved payments (both split and pay_for_all)
    const isInitiator = initiatedBy === currentUserId;
    const userRejected =
      paymentFlowStatus === "rejected" &&
      responses.some(
        (response) =>
          response.userId === currentUserId && response.response === "rejected"
      );

    // Users who approved the request (for split payments only, not pay_for_all)
    const userApproved =
      (paymentType === "split_evenly" || paymentType === "split_per_person") &&
      responses.some(
        (response) =>
          response.userId === currentUserId && response.response === "approved"
      );

    // For approved payments, show to users based on payment type
    const isApprovedPayment = paymentFlowStatus === "approved";

    // For approved pay_for_all, only show to initiator and users with amount to pay
    const isApprovedPayForAll =
      paymentType === "pay_for_all" && paymentFlowStatus === "approved";
    const userOrder = message?.orderId?.groupOrder?.userOrders?.find(
      (order) => {
        const userId = order.userId?._id || order.userId;
        return userId === currentUserId;
      }
    );
    const userAmountToPay = userOrder?.amountToPay || 0;
    const isApprovedPayForAllVisible =
      isApprovedPayForAll && (isInitiator || userAmountToPay > 0);

    // Payment request visibility rules based on payment type and status
    let shouldShowPaymentRequest = false;

    if (paymentFlowStatus === "pending_approval") {
      // For pending approval, show to all users who haven't responded yet or the initiator
      if (paymentType === "pay_for_all" || paymentType === "split_evenly") {
        // Show to initiator and all users who need to respond
        const hasUserResponded = responses.some(
          (response) =>
            response.userId === currentUserId &&
            (response.response === "approved" ||
              response.response === "rejected")
        );
        // Show to initiator or users who haven't responded
        shouldShowPaymentRequest = isInitiator || !hasUserResponded;

        // Also show to users who haven't responded yet (they need to see the request to respond)
        if (!isInitiator && !hasUserResponded) {
          // Check if current user is in the list of contributors (should see the request)
          const contributors = message?.orderId?.contributors || [];
          const isContributor = contributors.includes(currentUserId);
          shouldShowPaymentRequest = isContributor;
        }
      } else if (paymentType === "split_per_person") {
        // For split per person in pending state, show to all users
        shouldShowPaymentRequest = true;
      }
    } else if (paymentFlowStatus === "approved") {
      if (paymentType === "pay_for_all") {
        // Pay for All: only initiator sees request to pay full bill
        shouldShowPaymentRequest = isInitiator;
      } else if (paymentType === "split_evenly") {
        // Split Evenly: ALL accepted users see the payment request
        // Check if current user accepted the split evenly payment
        const responses =
          message?.orderId?.paymentFlow?.request?.responses || [];
        const userAccepted = responses.some(
          (response) =>
            response.userId === currentUserId &&
            response.response === "approved"
        );
        shouldShowPaymentRequest = userAccepted || isInitiator;
      } else if (paymentType === "split_per_person") {
        // Split Per Person: each user gets their own request
        // Check if this is a user-specific request for current user
        const messageText = message?.text || "";
        const currentUserFullName =
          currentUser?.fullName || currentUser?.userName || "";
        const isUserSpecificRequest =
          messageText.includes(`${currentUserFullName} needs to pay`) ||
          messageText.includes(`${currentUser?.userName} needs to pay`);
        shouldShowPaymentRequest = isUserSpecificRequest;
      }
    } else if (paymentFlowStatus === "rejected") {
      // For rejected payments, show to initiator and users who rejected
      const userRejected = responses.some(
        (response) =>
          response.userId === currentUserId && response.response === "rejected"
      );
      shouldShowPaymentRequest = isInitiator || userRejected;
    }

    // Don't show to business owners and team members of THIS specific order's business
    const orderBusinessId =
      message?.orderId?.businessId?._id || message?.orderId?.businessId;
    const orderLocationId =
      message?.orderId?.locationId?._id || message?.orderId?.locationId;
    let isCurrentUserOwnerOrTeamMemberForThisOrder = false;

    // Check if current user is owner/team member for THIS specific order's business
    const employeeInfo = currentUser?.employee;
    if (employeeInfo) {
      const employeeBusinessId =
        employeeInfo?.businessId?._id || employeeInfo?.businessId;
      const isSameBusiness =
        employeeBusinessId &&
        orderBusinessId &&
        employeeBusinessId === orderBusinessId;

      // Only check location if it's the same business
      let hasSameLocation = false;
      if (isSameBusiness) {
        const employeeLocationIds = Array.isArray(employeeInfo?.locationIds)
          ? employeeInfo.locationIds
          : [];
        hasSameLocation = employeeLocationIds.some((loc) => {
          const locId = loc?._id || loc;
          return orderLocationId && locId === orderLocationId;
        });
      }

      isCurrentUserOwnerOrTeamMemberForThisOrder = Boolean(
        isSameBusiness || hasSameLocation
      );
    }

    if (isCurrentUserOwnerOrTeamMemberForThisOrder) {
      shouldShowPaymentRequest = false;
    }

    // For split payments that are rejected, show to initiator, rejectors, and approvers
    const isRejectedSplitPayment =
      (paymentType === "split_evenly" || paymentType === "split_per_person") &&
      paymentFlowStatus === "rejected";

    const shouldShow = shouldShowPaymentRequest;

    if (!shouldShow) {
      return null; // Don't render the message for other users
    }
  }

  const isLocationMessage = message?.type === "location";

  return (
    <>
    <MessageBubbleContainer isOwn={isOwn} $fitContent={isLocationMessage}>
      {/* username and profile picture for other users */}
      {!isOwn && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            mb: 0.5,
            alignSelf: "flex-start",
          }}
        >
          <Avatar
            src={
              message.senderId?.image?.profilePic
                ? getImageDisplayUrl(message.senderId.image.profilePic)
                : message.senderId?.profilePic
                ? getImageDisplayUrl(message.senderId.profilePic)
                : undefined
            }
            alt={
              message.senderId?.fullName ||
              message.senderId?.userName ||
              "user"
            }
            sx={{
              width: { xs: 24, sm: 28 },
              height: { xs: 24, sm: 28 },
              bgcolor: "#7C3AED",
              fontSize: { xs: "0.7rem", sm: "0.8rem" },
            }}
          >
            {(
              message.senderId?.fullName ||
              message.senderId?.userName ||
              "u"
            )
              .charAt(0)
              .toUpperCase()}
          </Avatar>
          <Typography
            variant="caption"
            sx={{
              color: "#374151",
              fontSize: { xs: "0.75rem", sm: "0.8rem" },
              fontWeight: 600,
            }}
          >
            {message.senderId?.fullName || message.senderId?.userName || "user"}
          </Typography>
        </Box>
      )}

      {/* message content with media and text - positioned relatively for edge buttons */}
      <Box sx={{ position: "relative" }}>
        {/* left edge smile button - appears on right-aligned messages (own messages), only if not deleted */}
        {isOwn && !isDeletedForMe && !isDeletedForEveryone && !isEditing && (
          <Tooltip title="add reaction">
            <EdgeSmileButton
              position="left"
              onClick={(e) => handleSmileClick(e, "left")}
              size="small"
            >
              <EmojiEmotions fontSize="inherit" />
            </EdgeSmileButton>
          </Tooltip>
        )}

        {/* right edge smile button - appears on left-aligned messages (other users), only if not deleted */}
        {!isOwn && !isDeletedForMe && !isDeletedForEveryone && !isEditing && (
          <Tooltip title="add reaction">
            <EdgeSmileButton
              position="right"
              onClick={(e) => handleSmileClick(e, "right")}
              size="small"
            >
              <EmojiEmotions fontSize="inherit" />
            </EdgeSmileButton>
          </Tooltip>
        )}
        <Box sx={{ display: "flex", gap: 1 }}>
          {!isOwn && !isDeletedForMe && !isDeletedForEveryone && !isEditing && (
            <Tooltip title="more options">
              <IconButton
                size="small"
                onClick={handleOptionsClick}
                sx={{
                  width: "24px",
                  height: "24px",
                  mt: 1,
                  ml: 1,
                  mr: 1,
                  color: "rgba(0, 0, 0, 0.7)",
                  backgroundColor: isOwn
                    ? "rgba(255, 255, 255, 0.1)"
                    : "rgba(0, 0, 0, 0.05)",
                  borderRadius: "50%",
                  "&:hover": {
                    color: "rgba(0, 0, 0, 0.9)",
                    backgroundColor: isOwn
                      ? "rgba(255, 255, 255, 0.2)"
                      : "rgba(0, 0, 0, 0.1)",
                    transform: "scale(1.1)",
                  },
                  transition: "all 0.2s ease",
                }}
              >
                <MoreVert fontSize="small" />
              </IconButton>
            </Tooltip>
          )}

          <MessageContent
            isOwn={isOwn}
            isTemp={message.isTemp}
            isOrder={message?.type === "order"} // applies yellow background for order messages
            isCancellation={message?.type === "order_cancellation_request"} // applies gray background for cancellation messages
            isPayment={
              message?.type === "payment" || message?.type === "payment-request"
            } // applies special styling for payment messages
            isRequest={message?.type === "request"} // applies special styling for request messages
            idDraft={message?.orderId?.status == "draft"}
            isDisabled={message?.isLatestPaymentRequest === false} // applies disabled styling for old payment requests
            onContextMenu={
              !isDeletedForMe && !isDeletedForEveryone && !isEditing
                ? handleContextMenu
                : undefined
            }
          >
            {/* show deleted message indicator if deleted for current user or everyone */}
            {isDeletedForMe || isDeletedForEveryone ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  opacity: 0.7,
                  fontStyle: "italic",
                }}
              >
                <Delete
                  sx={{
                    fontSize: "1rem",
                    color: isOwn
                      ? "rgba(255, 255, 255, 0.7)"
                      : "rgba(0, 0, 0, 0.5)",
                  }}
                />
                <Typography
                  variant="body2"
                  sx={{
                    color: isOwn
                      ? "rgba(255, 255, 255, 0.7)"
                      : "rgba(0, 0, 0, 0.5)",
                    fontSize: "14px",
                    fontStyle: "italic",
                  }}
                >
                  {isDeletedForEveryone ? "message deleted" : "message deleted"}
                </Typography>
              </Box>
            ) : (
              <>
                {/* Forwarded message indicator - show when message is forwarded */}
                {message.isForwarded && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      marginBottom: theme.spacing(1),
                    }}
                  >
                    <Forward
                      sx={{
                        fontSize: "0.75rem",
                        color: isOwn ? "rgba(255, 255, 255, 0.7)" : "rgba(0, 0, 0, 0.5)",
                      }}
                    />
                    <Typography
                      variant="caption"
                      sx={{
                        color: isOwn ? "rgba(255, 255, 255, 0.7)" : "rgba(0, 0, 0, 0.5)",
                        fontWeight: 500,
                        fontSize: "0.7rem",
                        fontStyle: "italic",
                        textTransform: "lowercase",
                      }}
                    >
                      forwarded
                    </Typography>
                  </Box>
                )}

                {/* Reply preview - show when message is replying to another message */}
                {isValidReplyTo(message.replyTo, message.replyToText, message.replyToSender, message.replyToAttachments) && (
                  <Box
                    sx={{
                      padding: theme.spacing(1),
                      marginBottom: theme.spacing(1),
                      backgroundColor: isOwn
                        ? "rgba(255, 255, 255, 0.1)"
                        : "rgba(0, 0, 0, 0.05)",
                      borderRadius: theme.spacing(1),
                      borderLeft: "3px solid #7C3AED",
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                      "&:hover": {
                        backgroundColor: isOwn
                          ? "rgba(255, 255, 255, 0.15)"
                          : "rgba(0, 0, 0, 0.08)",
                      },
                    }}
                    onClick={() => {
                      // TODO: Scroll to the replied message when clicked
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        marginBottom: 0.5,
                      }}
                    >
                      <ReplyIcon
                        sx={{
                          fontSize: "0.75rem",
                          color: isOwn ? "rgba(255, 255, 255, 0.8)" : "#7C3AED",
                        }}
                      />
                      <Typography
                        variant="caption"
                        sx={{
                          color: isOwn ? "rgba(255, 255, 255, 0.8)" : "#7C3AED",
                          fontWeight: 600,
                          fontSize: "0.7rem",
                          textTransform: "lowercase",
                        }}
                      >
                        replying to {message.replyToSender || "message"}
                      </Typography>
                    </Box>

                    {/* reply preview content with optional media thumbnail */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        maxWidth: "100%",
                      }}
                    >
                      {/* show small thumbnail if original message had media */}
                      {Array.isArray(message.replyToAttachments) &&
                        message.replyToAttachments.length > 0 && (
                          <Box
                            sx={{
                              width: 36,
                              height: 36,
                              borderRadius: 1,
                              overflow: "hidden",
                              flexShrink: 0,
                              backgroundColor: "#f3f4f6",
                              border: isOwn
                                ? "1px solid rgba(255, 255, 255, 0.2)"
                                : "1px solid #e5e7eb",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            {message.replyToAttachments[0].type === "image" ? (
                              <img
                                src={
                                  message.replyToAttachments[0].url?.startsWith(
                                    "http"
                                  )
                                    ? message.replyToAttachments[0].url
                                    : MEDIA_URL +
                                      message.replyToAttachments[0].url
                                }
                                alt="media"
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "cover",
                                }}
                              />
                            ) : message.replyToAttachments[0].type ===
                              "video" ? (
                              <Box
                                sx={{
                                  color: isOwn
                                    ? "rgba(255, 255, 255, 0.8)"
                                    : "#6b7280",
                                }}
                              >
                                📹
                              </Box>
                            ) : (
                              <Box
                                sx={{
                                  color: isOwn
                                    ? "rgba(255, 255, 255, 0.8)"
                                    : "#6b7280",
                                }}
                              >
                                📎
                              </Box>
                            )}
                          </Box>
                        )}

                      <Typography
                        variant="caption"
                        sx={{
                          color: isOwn
                            ? "rgba(255, 255, 255, 0.7)"
                            : "rgba(0, 0, 0, 0.6)",
                          fontSize: "0.75rem",
                          fontStyle: "italic",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          maxWidth: "100%",
                        }}
                      >
                        {message.replyToText ||
                          (Array.isArray(message.replyToAttachments) &&
                          message.replyToAttachments.length > 0
                            ? `${message.replyToAttachments.length} media`
                            : "original message")}
                      </Typography>
                    </Box>
                  </Box>
                )}

                {/* location card: map image + address (like mobile), never show raw JSON */}
                {message.type === "location" && (() => {
                  const rawLoc = message.location || message.message || "";
                  const rawStr = typeof rawLoc === "string" ? rawLoc.trim() : "";
                  const isJson = rawStr.startsWith("{") || rawStr.startsWith("[");
                  let displayAddress = "";
                  if (message.location && String(message.location).trim() !== "" && message.location !== "null") {
                    const locStr = String(message.location).trim();
                    if (!locStr.startsWith("{") && !locStr.startsWith("[")) displayAddress = locStr;
                  }
                  if (!displayAddress && rawStr !== "" && rawStr !== "null") {
                    if (isJson) {
                      try {
                        const parsed = JSON.parse(rawStr);
                        displayAddress = parsed.address || parsed.name || "";
                      } catch {
                        // ignore parse error
                      }
                    } else {
                      displayAddress = rawStr;
                    }
                  }
                  const lat = message.latLong?.lat != null ? Number(message.latLong.lat) : NaN;
                  const lng = message.latLong?.long != null ? Number(message.latLong.long) : NaN;
                  const hasCoords = !Number.isNaN(lat) && !Number.isNaN(lng);
                  const mapKey = process.env.REACT_APP_GOOGLE_MAP_KEY;
                  const staticMapUrl =
                    mapKey && hasCoords
                      ? `https://maps.googleapis.com/maps/api/staticmap?center=${lat},${lng}&zoom=15&size=320x160&scale=2&markers=color:red%7C${lat},${lng}&key=${mapKey}`
                      : null;
                  if (!displayAddress && !staticMapUrl) return null;
                  return (
                    <Box
                      onClick={() =>
                        onViewLocation && message.type === "location" && onViewLocation(message)
                      }
                      sx={{
                        mb: 1,
                        overflow: "hidden",
                        borderRadius: 2,
                        border: "1px solid",
                        borderColor: isOwn
                          ? "rgba(255, 255, 255, 0.25)"
                          : "rgba(124, 58, 237, 0.2)",
                        bgcolor: isOwn
                          ? "rgba(255, 255, 255, 0.08)"
                          : "rgba(124, 58, 237, 0.06)",
                        maxWidth: "min(100%, 320px)",
                        minWidth: 0,
                        cursor: onViewLocation ? "pointer" : "default",
                        "&:hover": onViewLocation
                          ? {
                              bgcolor: isOwn
                                ? "rgba(255, 255, 255, 0.12)"
                                : "rgba(124, 58, 237, 0.1)",
                            }
                          : {},
                      }}
                    >
                      {staticMapUrl && (
                        <Box
                          component="img"
                          src={staticMapUrl}
                          alt="location map"
                          sx={{
                            width: "100%",
                            maxWidth: 320,
                            height: 160,
                            display: "block",
                            objectFit: "cover",
                          }}
                        />
                      )}
                      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, p: 1.5, minWidth: 0 }}>
                        <LocationIcon
                          sx={{
                            fontSize: 22,
                            color: isOwn ? "rgba(255,255,255,0.9)" : "#7C3AED",
                            flexShrink: 0,
                            mt: 0.25,
                          }}
                        />
                        <Typography
                          variant="body2"
                          sx={{
                            color: isOwn ? "#fff" : "#1F2937",
                            fontSize: "0.9rem",
                            fontWeight: 500,
                            lineHeight: 1.4,
                            wordBreak: "break-word",
                            overflowWrap: "break-word",
                            minWidth: 0,
                          }}
                        >
                          {displayAddress || "Location shared"}
                        </Typography>
                      </Box>
                    </Box>
                  );
                })()}

                {/* render contact card(s) when message type is contact (receive-only from mobile) */}
                {message.type === "contact" && (() => {
                  const fromAttachments = Array.isArray(message.attachments)
                    ? message.attachments
                        .filter((att) => att && att.type === "contact")
                        .map((att) => ({
                          name: att.displayName || att.name || att.contactName || "Contact",
                          mobile: att.phoneNumber || att.phone,
                        }))
                    : [];
                  let contactList = fromAttachments;
                  if (contactList.length === 0 && message.message && typeof message.message === "string") {
                    try {
                      const parsed = JSON.parse(message.message);
                      const name = parsed.name || parsed.displayName || "Contact";
                      const mobile = parsed.mobile || parsed.phone || parsed.phoneNumber;
                      if (name || mobile) contactList = [{ name, mobile }];
                    } catch {
                      // ignore parse error
                    }
                  }
                  if (contactList.length === 0) return null;
                  return (
                    <Box sx={{ mb: formattedMessage.text ? 1 : 0 }}>
                      {contactList.map((contact, idx) => (
                        <Box
                          key={`contact-${message._id ?? "msg"}-${idx}`}
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 1,
                            p: 1.5,
                            borderRadius: 2,
                            border: "1px solid",
                            borderColor: isOwn
                              ? "rgba(255, 255, 255, 0.3)"
                              : "rgba(124, 58, 237, 0.2)",
                            bgcolor: isOwn
                              ? "rgba(255, 255, 255, 0.08)"
                              : "rgba(124, 58, 237, 0.06)",
                            maxWidth: "100%",
                            minWidth: "200px",
                          }}
                        >
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                            <PersonIcon
                              sx={{
                                fontSize: 28,
                                color: isOwn ? "rgba(255,255,255,0.9)" : "#7C3AED",
                              }}
                            />
                            <Box>
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 600,
                                  color: isOwn ? "#fff" : "#1F2937",
                                  fontSize: "0.9rem",
                                }}
                              >
                                {contact.name}
                              </Typography>
                              {contact.mobile && (
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: isOwn ? "rgba(255,255,255,0.8)" : "#6B7280",
                                    fontSize: "0.8rem",
                                  }}
                                >
                                  {contact.mobile}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  );
                })()}

                {/* render voice message player if message type is voice (receive-only from mobile) */}
                {message.type === "voice" &&
                  Array.isArray(message.attachments) &&
                  message.attachments.length > 0 &&
                  message.attachments.some(
                    (att) => att.type === "voice" || att.type === "audio"
                  ) && (
                    <Box
                      sx={{
                        mb: formattedMessage.text ? 1 : 0,
                        width: "100%",
                        maxWidth: "300px",
                      }}
                    >
                      {message.attachments
                        .filter(
                          (att) => att.type === "voice" || att.type === "audio"
                        )
                        .map((voiceAttachment, index) => (
                          <Box
                            key={index}
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "flex-start",
                              gap: 1,
                              p: 2,
                              backgroundColor: isOwn
                                ? "rgba(255, 255, 255, 0.1)"
                                : "rgba(124, 58, 237, 0.1)",
                              borderRadius: 2,
                              width: "100%",
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                width: "100%",
                              }}
                            >
                              <AudioIcon
                                sx={{
                                  fontSize: 24,
                                  color: isOwn
                                    ? "rgba(255, 255, 255, 0.9)"
                                    : "#7C3AED",
                                }}
                              />
                              <audio
                                controls
                                src={getImageDisplayUrl(voiceAttachment.url, AUDIO_URL)}
                                style={{
                                  flex: 1,
                                  height: "32px",
                                }}
                              />
                            </Box>
                            {voiceAttachment.duration &&
                              voiceAttachment.duration !== "0" && (
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: isOwn
                                      ? "rgba(255, 255, 255, 0.7)"
                                      : "rgba(0, 0, 0, 0.6)",
                                    fontSize: "0.7rem",
                                  }}
                                >
                                  Duration: {formatDuration(voiceAttachment.duration)}
                                </Typography>
                              )}
                          </Box>
                        ))}
                    </Box>
                  )}

                {/* render media attachments if present (excluding voice/contact if already shown above) */}
                {(() => {
                  const forGrid =
                    message.type === "voice"
                      ? (message.attachments || []).filter(
                          (att) =>
                            att.type !== "voice" && att.type !== "audio"
                        )
                      : message.type === "contact"
                        ? (message.attachments || []).filter(
                            (att) => att.type !== "contact"
                          )
                        : message.attachments || [];
                  return forGrid.length > 0 ? (
                    <Box sx={{ mb: formattedMessage.text ? 1 : 0 }}>
                      {renderMediaGrid(forGrid)}
                    </Box>
                  ) : null;
                })()}

                {/* render message text or edit interface */}
                {/* Hide for location (address in card only) and contact (raw JSON); show for others */}
                {(() => {
                  const raw = (message?.text || message?.message || "").trim();
                  const isJson = typeof raw === "string" && (raw.startsWith("{") || raw.startsWith("["));
                  const hideText =
                    message?.type === "location" ||
                    (message?.type === "contact" && isJson);
                  return (formattedMessage.text || message?.isAiStreaming) && !hideText;
                })() && (
                  <>
                    {isEditing ? (
                      // edit mode - show text field with save/cancel buttons
                      <Box sx={{ width: "100%" }}>
                        <TextField
                          fullWidth
                          multiline
                          value={editText}
                          onChange={(e) => setEditText(e.target.value)}
                          onKeyDown={handleEditKeyPress}
                          variant="outlined"
                          size="small"
                          placeholder="edit message..."
                          autoFocus
                          sx={{
                            mb: 1,
                            "& .MuiOutlinedInput-root": {
                              backgroundColor: isOwn
                                ? "rgba(255, 255, 255, 0.1)"
                                : "rgba(0, 0, 0, 0.05)",
                              color: isOwn ? "#FFFFFF" : "#000000",
                              fontSize: "15px",
                              "& fieldset": {
                                borderColor: isOwn
                                  ? "rgba(255, 255, 255, 0.3)"
                                  : "rgba(0, 0, 0, 0.2)",
                              },
                              "&:hover fieldset": {
                                borderColor: isOwn
                                  ? "rgba(255, 255, 255, 0.5)"
                                  : "rgba(0, 0, 0, 0.3)",
                              },
                              "&.Mui-focused fieldset": {
                                borderColor: isOwn ? "#FFFFFF" : "#7C3AED",
                              },
                            },
                            "& .MuiInputBase-input::placeholder": {
                              color: isOwn
                                ? "rgba(255, 255, 255, 0.7)"
                                : "rgba(0, 0, 0, 0.5)",
                              opacity: 1,
                            },
                          }}
                        />
                        <Box
                          sx={{
                            display: "flex",
                            gap: 1,
                            justifyContent: "flex-end",
                          }}
                        >
                          <Button
                            size="small"
                            onClick={handleCancelEdit}
                            sx={{
                              color: isOwn
                                ? "rgba(255, 255, 255, 0.8)"
                                : "rgba(0, 0, 0, 0.6)",
                              fontSize: "12px",
                              minWidth: "auto",
                              px: 1,
                              "&:hover": {
                                backgroundColor: isOwn
                                  ? "rgba(255, 255, 255, 0.1)"
                                  : "rgba(0, 0, 0, 0.05)",
                              },
                            }}
                          >
                            cancel
                          </Button>
                          <Button
                            size="small"
                            onClick={handleSaveEdit}
                            disabled={
                              !editText.trim() ||
                              editText.trim() === message.text
                            }
                            sx={{
                              color: isOwn ? "#FFFFFF" : "#7C3AED",
                              fontSize: "12px",
                              fontWeight: 600,
                              minWidth: "auto",
                              px: 1,
                              "&:hover": {
                                backgroundColor: isOwn
                                  ? "rgba(255, 255, 255, 0.1)"
                                  : "rgba(124, 58, 237, 0.1)",
                              },
                              "&.Mui-disabled": {
                                color: isOwn
                                  ? "rgba(255, 255, 255, 0.3)"
                                  : "rgba(0, 0, 0, 0.3)",
                              },
                            }}
                          >
                            save
                          </Button>
                        </Box>
                      </Box>
                    ) : message?.type === "request" ? (
                      // treat message layout - displays different content for initiator vs recipients
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 2,
                          width: "100%",
                          minWidth: "280px",
                          maxWidth: "380px",
                        }}
                      >
                        {isOwn ? (
                          // For the person who initiated the request - show image type message
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 1.5,
                              alignItems: "center",
                              textAlign: "center",
                            }}
                          >
                            {/* Celebration emoji - no dashed box */}
                            <Typography
                              variant="h2"
                              sx={{
                                fontSize: "64px",
                                lineHeight: 1,
                                mb: 0.5,
                              }}
                            >
                              🎉
                            </Typography>

                            <Typography
                              variant="body1"
                              sx={{
                                color: "#FFFFFF",
                                fontSize: "16px",
                                fontWeight: 600,
                                lineHeight: 1.3,
                              }}
                            >
                              {formattedMessage.text?.includes(
                                "treat the group"
                              )
                                ? "You offered to treat the group!"
                                : formattedMessage.text?.includes(
                                    "split the bill evenly"
                                  )
                                ? "You requested to split the bill evenly"
                                : formattedMessage.text?.includes(
                                    "split the bill per person"
                                  )
                                ? "You requested to split the bill per person"
                                : "You initiated a payment request"}
                            </Typography>

                            <Typography
                              variant="body2"
                              sx={{
                                color: "rgba(255, 255, 255, 0.8)",
                                fontSize: "14px",
                                fontWeight: 400,
                                lineHeight: 1.3,
                              }}
                            >
                              Awaiting responses from group members
                            </Typography>
                          </Box>
                        ) : (
                          // For recipients - show the original request message with response buttons
                          <>
                            <Typography
                              variant="body2"
                              sx={{
                                color: isOwn ? "#FFFFFF" : "#000000",
                                fontSize: "15px",
                                fontWeight: 400,
                                lineHeight: 1.4,
                              }}
                            >
                              {generatePaymentMessageText()}
                            </Typography>

                            {/* Timer display */}

                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                mb: 1,
                              }}
                            >
                              <AccessTime
                                sx={{ fontSize: 16, color: "#6B7280" }}
                              />

                              <MessageTimer
                                createdAt={message?.createdAt}
                                onReachZero={() => {
                                  // This runs when timer hits zero
                                  // alert("Message expired!");
                                  // Hide message, update status, etc.

                                  if (!apiCalled.current) {
                                    const paymentType =
                                      message?.orderId?.paymentFlow?.type ||
                                      message?.orderId?.paymentType;

                                    if (paymentType === "pay_for_all") {
                                      // Auto-accept for pay_for_all
                                      if (
                                        onPaymentResponse &&
                                        message?.orderId?._id
                                      ) {
                                        apiCalled.current = true;
                                        onPaymentResponse(
                                          message.orderId._id,
                                          "approved"
                                        );
                                      }
                                    } else if (paymentType === "split_evenly") {
                                      // Auto-reject for split_evenly
                                      if (
                                        onPaymentResponse &&
                                        message?.orderId?._id
                                      ) {
                                        apiCalled.current = true;
                                        onPaymentResponse(
                                          message.orderId._id,
                                          "rejected"
                                        );
                                      }
                                    }
                                  }

                                  setTimerActive(false);
                                  setTimeRemaining(0);
                                }}
                                onAlreadyReachZero={() => {
                                  setTimerActive(false);
                                  setTimeRemaining(0);
                                  // This runs when timer hits zero
                                  // alert("Message expired!");
                                  // Hide message, update status, etc.
                                }}
                              />
                            </Box>

                            {/* Response buttons - only for recipients */}
                            <Box
                              sx={{
                                display: "flex",
                                gap: 1,
                                flexWrap: "wrap",
                              }}
                            >
                              <Button
                                variant="contained"
                                // disabled={
                                //   !timerActive ||
                                //   timeRemaining === 0 ||
                                //   userHasResponded
                                // }
                                sx={{
                                  backgroundColor:
                                    timerActive &&
                                    timeRemaining > 0 &&
                                    !userHasResponded
                                      ? "#7C3AED"
                                      : "#D1D5DB",
                                  color:
                                    timerActive &&
                                    timeRemaining > 0 &&
                                    !userHasResponded
                                      ? "white"
                                      : "#9CA3AF",
                                  fontWeight: 700,
                                  fontSize: "14px",
                                  borderRadius: 2,
                                  px: 2,
                                  py: 1,
                                  textTransform: "none",
                                  cursor:
                                    timerActive &&
                                    timeRemaining > 0 &&
                                    !userHasResponded
                                      ? "pointer"
                                      : "not-allowed",
                                  "&:hover": {
                                    backgroundColor:
                                      timerActive &&
                                      timeRemaining > 0 &&
                                      !userHasResponded
                                        ? "#6D28D9"
                                        : "#D1D5DB",
                                  },
                                }}
                                onClick={() => {
                                  if (
                                    timerActive &&
                                    timeRemaining > 0 &&
                                    !userHasResponded &&
                                    !apiCalled.current
                                  ) {
                                    // Clear timer first
                                    if (timerRef.current) {
                                      clearTimeout(timerRef.current);
                                      timerRef.current = null;
                                    }

                                    // Handle thank you response - approve payment
                                    if (
                                      onPaymentResponse &&
                                      message?.orderId?._id
                                    ) {
                                      apiCalled.current = true;
                                      onPaymentResponse(
                                        message.orderId._id,
                                        "approved"
                                      );
                                    }
                                    setUserHasResponded(true); // Mark user as responded
                                    // setTimerActive(false); // Stop timer after action
                                    setAutoResponseTriggered(false); // Manual response, not auto
                                  }
                                }}
                              >
                                thank you!
                              </Button>
                              <Button
                                variant="contained"
                                disabled={
                                  !timerActive ||
                                  timeRemaining === 0 ||
                                  userHasResponded
                                }
                                sx={{
                                  backgroundColor:
                                    timerActive &&
                                    timeRemaining > 0 &&
                                    !userHasResponded
                                      ? "#A78BFA"
                                      : "#D1D5DB",
                                  color:
                                    timerActive &&
                                    timeRemaining > 0 &&
                                    !userHasResponded
                                      ? "white"
                                      : "#9CA3AF",
                                  fontWeight: 700,
                                  fontSize: "14px",
                                  borderRadius: 2,
                                  px: 2,
                                  py: 1,
                                  textTransform: "none",
                                  cursor:
                                    timerActive &&
                                    timeRemaining > 0 &&
                                    !userHasResponded
                                      ? "pointer"
                                      : "not-allowed",
                                  "&:hover": {
                                    backgroundColor:
                                      timerActive &&
                                      timeRemaining > 0 &&
                                      !userHasResponded
                                        ? "#8B5CF6"
                                        : "#D1D5DB",
                                  },
                                }}
                                onClick={() => {
                                  if (
                                    timerActive &&
                                    timeRemaining > 0 &&
                                    !userHasResponded &&
                                    !apiCalled.current
                                  ) {
                                    // Clear timer first
                                    if (timerRef.current) {
                                      clearTimeout(timerRef.current);
                                      timerRef.current = null;
                                    }

                                    // Handle thank you, i got mine response - reject payment
                                    if (
                                      onPaymentResponse &&
                                      message?.orderId?._id
                                    ) {
                                      apiCalled.current = true;
                                      onPaymentResponse(
                                        message.orderId._id,
                                        "rejected"
                                      );
                                    }
                                    setUserHasResponded(true); // Mark user as responded
                                    // setTimerActive(false); // Stop timer after action
                                    setAutoResponseTriggered(false); // Manual response, not auto
                                  }
                                }}
                              >
                                thank you, i got mine
                              </Button>
                            </Box>

                            {/* User response indicator */}
                            {userHasResponded && (
                              <Box
                                sx={{
                                  mt: 1,
                                  p: 1,
                                  backgroundColor: autoResponseTriggered
                                    ? "#FEF3C7"
                                    : "#F3F4F6",
                                  borderRadius: 1,
                                  border: autoResponseTriggered
                                    ? "1px solid #F59E0B"
                                    : "1px solid #E5E7EB",
                                }}
                              >
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: autoResponseTriggered
                                      ? "#92400E"
                                      : "#6B7280",
                                    fontSize: "12px",
                                    fontWeight: 500,
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.5,
                                  }}
                                >
                                  {autoResponseTriggered ? (
                                    <>
                                      ⏰ Timer expired - Response was
                                      automatically processed
                                    </>
                                  ) : (
                                    <>✓ You have responded to this request</>
                                  )}
                                </Typography>
                              </Box>
                            )}
                          </>
                        )}
                      </Box>
                    ) : message?.type === "payment" ? (
                      // payment message layout - displays payment request with clickable tip text
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 1,
                          width: "100%",
                          minWidth: "280px",
                          maxWidth: "380px",
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            color: isOwn ? "#FFFFFF" : "#000000",
                            fontSize: "15px",
                            fontWeight: 400,
                            lineHeight: 1.4,
                          }}
                        >
                          {generatePaymentMessageText()}{" "}
                          {message?.type === "payment_request" &&
                            (() => {
                              // Check if tip here button should be disabled
                              const orderId = message?.orderId;
                              const paymentFlow = orderId?.paymentFlow;
                              const paymentFlowStatus = paymentFlow?.status;
                              const isPaymentInitiated =
                                paymentFlowStatus === "pending_approval" ||
                                paymentFlowStatus === "approved";

                              // Get current user's payment type
                              const groupOrder = orderId?.groupOrder;
                              const userOrders = groupOrder?.userOrders || [];
                              const currentUserId =
                                currentUser?.userId ?? storedUserId;
                              const currentUserOrder = userOrders.find(
                                (userOrder) => {
                                  const userId =
                                    userOrder.userId?._id || userOrder.userId;
                                  return userId === currentUserId;
                                }
                              );
                              const hasUserChosenSplitPerPerson =
                                currentUserOrder?.paymentType ===
                                "split_per_person";

                              // Disable tip here if payment is initiated AND (user has chosen split_per_person OR user's paymentType is not null)
                              const hasUserPaymentTypeNull =
                                currentUserOrder?.paymentType === null;

                              // Check if someone else is paying for all (pay_for_all scenario)
                              const someoneElsePayingForAll = userOrders.some(
                                (userOrder) =>
                                  userOrder.paymentType === "pay_for_all" &&
                                  (userOrder.userId?._id ||
                                    userOrder.userId) !== currentUserId
                              );

                              const shouldDisableTipHere =
                                isPaymentInitiated &&
                                (hasUserChosenSplitPerPerson ||
                                  !hasUserPaymentTypeNull ||
                                  someoneElsePayingForAll);

                              return (
                                <Box
                                  component="span"
                                  sx={{
                                    color: shouldDisableTipHere
                                      ? "#9CA3AF"
                                      : "#7C3AED",
                                    fontWeight: 700,
                                    textDecoration: shouldDisableTipHere
                                      ? "none"
                                      : "underline",
                                    cursor: shouldDisableTipHere
                                      ? "not-allowed"
                                      : "pointer",
                                    opacity: shouldDisableTipHere ? 0.6 : 1,
                                    "&:hover": {
                                      color: shouldDisableTipHere
                                        ? "#9CA3AF"
                                        : "#6D28D9",
                                      textDecoration: shouldDisableTipHere
                                        ? "none"
                                        : "underline",
                                    },
                                  }}
                                  onClick={() => {
                                    if (shouldDisableTipHere) {
                                      return;
                                    }

                                    onOpenPaymentDialog?.(message);
                                  }}
                                >
                                  tip here.
                                </Box>
                              );
                            })()}
                          {!isOwn &&
                            message?.orderId?.paymentFlow?.status ===
                              "pending_approval" && (
                              <Box
                                component="span"
                                sx={{
                                  color: "#10B981",
                                  fontWeight: 600,
                                  fontSize: "14px",
                                  ml: 1,
                                }}
                              >
                                (Payment flow initiated)
                              </Box>
                            )}
                        </Typography>
                      </Box>
                    ) : message?.type === "payment-request" ? (
                      // payment-request message layout - displays payment request with image and order details
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 2.5,
                          width: "100%",
                          minWidth: "280px",
                          maxWidth: "380px",
                          padding: "2px 0",
                          overflow: "hidden",
                        }}
                      >
                        {/* left side - image from orderId.imageVideo */}
                        <Box
                          sx={{
                            width: 80,
                            height: 80,
                            borderRadius: 1.5,
                            backgroundColor: "#f3f4f6",
                            border: "1px solid #e5e7eb",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                            overflow: "hidden",
                          }}
                        >
                          {message?.orderId?.imageVideo &&
                          Array.isArray(message.orderId.imageVideo) &&
                          message.orderId.imageVideo.length > 0 ? (
                            <img
                              src={`${IMAGE_URL}${message.orderId.imageVideo[0]}`}
                              alt="order item"
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                              }}
                            />
                          ) : (
                            <Typography
                              variant="caption"
                              sx={{
                                color: "#6b7280",
                                fontSize: "11px",
                                textAlign: "center",
                                fontWeight: 500,
                              }}
                            >
                              image placeholder
                            </Typography>
                          )}
                        </Box>

                        {/* right side - payment request details */}
                        <Box
                          sx={{
                            flex: 1,
                            minWidth: 0,
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "space-between",
                            height: "80px",
                            overflow: "hidden",
                          }}
                        >
                          {/* top section - payment request text */}
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "flex-start",
                              mb: 0.3,
                              minHeight: "20px",
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                fontSize: "14px",
                                fontWeight: 400,
                                color: isOwn ? "#FFFFFF" : "#000000",
                                lineHeight: 1.3,
                                flex: 1,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {(() => {
                                // Check if current user rejected the payment request
                                const initiatedBy =
                                  message?.orderId?.paymentFlow?.initiatedBy;
                                const responses =
                                  message?.orderId?.paymentFlow?.request
                                    ?.responses || [];
                                const paymentFlowStatus =
                                  message?.orderId?.paymentFlow?.status;
                                const paymentType =
                                  message?.orderId?.paymentType;

                                const isInitiator =
                                  initiatedBy === currentUserId;
                                const isRejectedPayForAll =
                                  paymentFlowStatus === "rejected" &&
                                  paymentType === "pay_for_all";

                                if (isRejectedPayForAll) {
                                  // Show message for both users when pay_for_all is rejected
                                  if (isInitiator) {
                                    return "Pay for all was rejected. You need to pay for your own order";
                                  } else {
                                    return "You need to pay for your own order";
                                  }
                                }

                                // Get current user's order details
                                const userOrder =
                                  message?.orderId?.groupOrder?.userOrders?.find(
                                    (order) => order.userId === currentUserId
                                  );
                                const userAmountToPay =
                                  userOrder?.amountToPay || 0;
                                const userRemainingAmount =
                                  userOrder?.remainingAmount || 0;
                                const userAmountPaid =
                                  userOrder?.amountPaid || 0;
                                const upfrontPercentage =
                                  message?.orderId?.paymentSettings
                                    ?.upfrontPercentage || 20;

                                // Check if current user has zero amount to pay
                                if (userAmountToPay === 0) {
                                  return "You have no amount to pay for this order";
                                }

                                // For split_per_person payments, show personalized message
                                if (paymentType === "split_per_person") {
                                  const userName =
                                    userOrder?.userFullName || "You";
                                  const currencyCode = message?.orderId?.currency;
                                  const currencySymbol = getCurrencySymbol(currencyCode);

                                  if (
                                    userAmountPaid > 0 &&
                                    userRemainingAmount > 0
                                  ) {
                                    // User has paid some but still has remaining amount
                                    return `${userName} needs to pay ${currencySymbol}${userRemainingAmount.toFixed(
                                      2
                                    )} for their order (${currencySymbol}${userAmountPaid.toFixed(
                                      2
                                    )} already paid)`;
                                  } else if (
                                    userAmountPaid > 0 &&
                                    userRemainingAmount === 0
                                  ) {
                                    // User has fully paid
                                    return `${userName} has fully paid for their order`;
                                  } else {
                                    // User hasn't paid anything yet
                                    return `${userName} needs to pay ${currencySymbol}${userAmountToPay.toFixed(
                                      2
                                    )} for their order`;
                                  }
                                }

                                // For other payment types, show generated message
                                return generatePaymentMessageText();
                              })()}
                            </Typography>
                          </Box>

                          {/* middle section - payment type and amount */}
                          <Box sx={{ mb: 0.2, flex: 1 }}>
                            <Typography
                              variant="body2"
                              sx={{
                                fontSize: "12px",
                                color: isOwn
                                  ? "rgba(255, 255, 255, 0.8)"
                                  : "#6b7280",
                                mb: 0.2,
                                textTransform: "lowercase",
                                fontWeight: 500,
                              }}
                            >
                              {(() => {
                                // Check if current user rejected the payment request
                                const initiatedBy =
                                  message?.orderId?.paymentFlow?.initiatedBy;
                                const responses =
                                  message?.orderId?.paymentFlow?.request
                                    ?.responses || [];
                                const paymentFlowStatus =
                                  message?.orderId?.paymentFlow?.status;

                                const isRejectedPayForAll =
                                  paymentFlowStatus === "rejected" &&
                                  message?.orderId?.paymentType ===
                                    "pay_for_all";

                                if (isRejectedPayForAll) {
                                  // Show "split per person" for both users when pay_for_all is rejected
                                  return "split per person";
                                }

                                // Show original payment type for other cases
                                return message?.orderId?.paymentType ===
                                  "split_per_person"
                                  ? "split per person"
                                  : message?.orderId?.paymentType ===
                                    "split_evenly"
                                  ? "split evenly"
                                  : "pay for all";
                              })()}
                            </Typography>
                          </Box>

                          {/* bottom section - total amount and pay button */}
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              mt: "auto",
                              minHeight: "20px",
                            }}
                          >
                            {/* total amount on the left */}
                            <Typography
                              variant="h6"
                              sx={{
                                fontSize: "16px",
                                fontWeight: 700,
                                color: isOwn ? "#FFFFFF" : "#000000",
                                lineHeight: 1,
                              }}
                            >
                              {(() => {
                                // Check if current user rejected the payment request
                                const initiatedBy =
                                  message?.orderId?.paymentFlow?.initiatedBy;
                                const responses =
                                  message?.orderId?.paymentFlow?.request
                                    ?.responses || [];
                                const paymentFlowStatus =
                                  message?.orderId?.paymentFlow?.status;

                                const isRejectedPayForAll =
                                  paymentFlowStatus === "rejected" &&
                                  message?.orderId?.paymentType ===
                                    "pay_for_all";

                                const currencyCode = message?.orderId?.currency;
                                const currencySymbol = getCurrencySymbol(currencyCode);

                                if (isRejectedPayForAll) {
                                  // Show individual upfront amount for both users when pay_for_all is rejected
                                  const userOrder =
                                    message?.orderId?.groupOrder?.userOrders?.find(
                                      (order) => order.userId === currentUserId
                                    );
                                  const individualAmount =
                                    userOrder?.subtotal || 0;
                                  const upfrontPercentage =
                                    message?.orderId?.paymentSettings
                                      ?.upfrontPercentage;

                                  // Show "No payment required" if user has zero amount
                                  if (individualAmount === 0) {
                                    return "No payment required";
                                  }

                                  if (
                                    upfrontPercentage &&
                                    upfrontPercentage > 0
                                  ) {
                                    const upfrontAmount =
                                      (individualAmount * upfrontPercentage) /
                                      100;
                                    const roundedUpfrontAmount =
                                      Math.round(upfrontAmount * 100) / 100;
                                    return `upfront: ${currencySymbol}${roundedUpfrontAmount.toFixed(
                                      2
                                    )}`;
                                  } else {
                                    return `total: ${currencySymbol}${individualAmount.toFixed(
                                      2
                                    )}`;
                                  }
                                }

                                // Show amount based on payment type
                                const paymentType =
                                  message?.orderId?.paymentType;
                                const upfrontPercentage =
                                  message?.orderId?.paymentSettings
                                    ?.upfrontPercentage;

                                if (
                                  paymentType === "split_evenly" ||
                                  paymentType === "split_per_person"
                                ) {
                                  // For split payments, use individual user's amount
                                  const userOrder =
                                    message?.orderId?.groupOrder?.userOrders?.find(
                                      (order) => order.userId === currentUserId
                                    );
                                  const individualAmount =
                                    userOrder?.amountToPay || 0;
                                  const userRemainingAmount =
                                    userOrder?.remainingAmount || 0;
                                  const userAmountPaid =
                                    userOrder?.amountPaid || 0;

                                  // Show "No payment required" if user has zero amount to pay
                                  if (individualAmount === 0) {
                                    return "No payment required";
                                  }

                                  // For split_per_person, show remaining amount if user has already paid some
                                  if (
                                    paymentType === "split_per_person" &&
                                    userAmountPaid > 0 &&
                                    userRemainingAmount > 0
                                  ) {
                                    if (
                                      upfrontPercentage &&
                                      upfrontPercentage > 0
                                    ) {
                                      const remainingUpfrontAmount =
                                        (userRemainingAmount *
                                          upfrontPercentage) /
                                        100;
                                      const roundedRemainingAmount =
                                        Math.round(
                                          remainingUpfrontAmount * 100
                                        ) / 100;
                                      return `remaining: ${currencySymbol}${roundedRemainingAmount.toFixed(
                                        2
                                      )}`;
                                    } else {
                                      return `remaining: ${currencySymbol}${userRemainingAmount.toFixed(
                                        2
                                      )}`;
                                    }
                                  } else if (
                                    paymentType === "split_per_person" &&
                                    userAmountPaid > 0 &&
                                    userRemainingAmount === 0
                                  ) {
                                    return "Fully paid";
                                  } else {
                                    // Show upfront amount for first payment
                                    if (
                                      upfrontPercentage &&
                                      upfrontPercentage > 0
                                    ) {
                                      const upfrontAmount =
                                        (individualAmount * upfrontPercentage) /
                                        100;
                                      const roundedUpfrontAmount =
                                        Math.round(upfrontAmount * 100) / 100;
                                      return `upfront: ${currencySymbol}${roundedUpfrontAmount.toFixed(
                                        2
                                      )}`;
                                    } else {
                                      return `total: ${currencySymbol}${individualAmount.toFixed(
                                        2
                                      )}`;
                                    }
                                  }
                                } else {
                                  // For pay_for_all, use group total
                                  const totalAmount =
                                    message?.orderId?.groupSubtotal ||
                                    message?.orderId?.totalAmount ||
                                    0;

                                  if (
                                    upfrontPercentage &&
                                    upfrontPercentage > 0
                                  ) {
                                    const upfrontAmount =
                                      (totalAmount * upfrontPercentage) / 100;
                                    const roundedUpfrontAmount =
                                      Math.round(upfrontAmount * 100) / 100;
                                    return `upfront: ${currencySymbol}${roundedUpfrontAmount.toFixed(
                                      2
                                    )} `;
                                  } else {
                                    return `total: ${currencySymbol}${totalAmount.toFixed(2)}`;
                                  }
                                }
                              })()}
                            </Typography>

                            {/* pay button on the right - only for non-owners and latest payment request */}
                            {!isOwn &&
                              message.isLatestPaymentRequest !== false &&
                              (() => {
                                // Check if we should show the pay button
                                const paymentType =
                                  message?.orderId?.paymentType;
                                const totalPaidAmount =
                                  message?.orderId?.totalPaidAmount || 0;
                                const responses =
                                  message?.orderId?.paymentFlow?.request
                                    ?.responses || [];
                                const paymentFlowStatus =
                                  message?.orderId?.paymentFlow?.status;
                                const initiatedBy =
                                  message?.orderId?.paymentFlow?.initiatedBy;
                                const isInitiator =
                                  initiatedBy === currentUserId;

                                // Get current user's order to check their payment status
                                const userOrder =
                                  message?.orderId?.groupOrder?.userOrders?.find(
                                    (order) => order.userId === currentUserId
                                  );
                                const userAmountPaid =
                                  userOrder?.amountPaid || 0;
                                const userPaymentStatus =
                                  userOrder?.paymentStatus;
                                const userAmountToPay =
                                  userOrder?.amountToPay || 0;

                                // Case 1: Hide pay button if payment is approved and fully paid (including upfront)
                                const isApprovedAndFullyPaid =
                                  paymentType === "pay_for_all" &&
                                  responses.length > 0 &&
                                  responses.every(
                                    (response) =>
                                      response.response === "approved"
                                  ) &&
                                  totalPaidAmount > 0 &&
                                  message?.orderId?.paymentCompleted === true;

                                // Case 2: Hide pay button if user has already paid upfront
                                const hasUserPaidUpfront =
                                  userAmountPaid > 0 &&
                                  (userPaymentStatus === "upfront_paid" ||
                                    userPaymentStatus === "paid");

                                // Case 2.5: Hide pay button if user has zero amount to pay (except for approved pay_for_all initiator)
                                const hasZeroAmountToPay =
                                  userAmountToPay === 0;

                                // Case 3: For approved pay_for_all requests, show pay button to initiator if upfront payment is still needed
                                const isApprovedPayForAllWithUpfrontNeeded =
                                  paymentType === "pay_for_all" &&
                                  paymentFlowStatus === "approved" &&
                                  isInitiator &&
                                  !hasUserPaidUpfront &&
                                  message?.orderId?.paymentSettings
                                    ?.upfrontPercentage > 0;

                                // Case 3.5: For approved pay_for_all initiator with amount to pay, show pay button
                                const isApprovedPayForAllInitiatorWithAmount =
                                  paymentType === "pay_for_all" &&
                                  paymentFlowStatus === "approved" &&
                                  isInitiator &&
                                  !hasUserPaidUpfront &&
                                  userAmountToPay > 0;

                                // Case 4: For rejected pay_for_all requests, show pay button to BOTH users (but not if they paid upfront)
                                const isRejectedPayForAll =
                                  paymentType === "pay_for_all" &&
                                  paymentFlowStatus === "rejected" &&
                                  !hasUserPaidUpfront;

                                // Case 4.5: For approved pay_for_all where current user rejected, show pay button for individual amount
                                const userRejectedPayForAll =
                                  paymentType === "pay_for_all" &&
                                  paymentFlowStatus === "approved" &&
                                  responses.some(
                                    (response) =>
                                      response.userId === currentUserId &&
                                      response.response === "rejected"
                                  ) &&
                                  !hasUserPaidUpfront &&
                                  userAmountToPay > 0;

                                // Case 5: For other payment types, show pay button normally (but not if they paid upfront)
                                const isOtherPaymentType =
                                  paymentType !== "pay_for_all" &&
                                  !hasUserPaidUpfront;

                                // Show pay button if:
                                // 1. BUTTON MUST NOT BE DISABLED (buttonDisabled must be false or undefined) - CHECK FIRST, AND
                                // 2. Not fully paid, AND
                                // 3. User hasn't paid upfront, AND
                                // 4. Either user has amount to pay OR it's approved pay_for_all with upfront needed, AND
                                // 5. Either it's an approved pay_for_all with upfront needed, OR approved pay_for_all initiator with amount, OR rejected pay_for_all, OR user rejected pay_for_all, OR other payment type
                                
                                // If button is disabled, never show the button regardless of other conditions
                                if (message?.buttonDisabled === true) {
                                  return false;
                                }
                                
                                const shouldShowPayButton =
                                  !isApprovedAndFullyPaid &&
                                  !hasUserPaidUpfront &&
                                  (!hasZeroAmountToPay ||
                                    isApprovedPayForAllWithUpfrontNeeded ||
                                    isApprovedPayForAllInitiatorWithAmount ||
                                    userRejectedPayForAll) &&
                                  (isApprovedPayForAllWithUpfrontNeeded ||
                                    isApprovedPayForAllInitiatorWithAmount ||
                                    isRejectedPayForAll ||
                                    userRejectedPayForAll ||
                                    isOtherPaymentType);
                                
                                // Hide pay button for "wants to pay for their own items" messages if split_per_person
                                const isIndividualPaymentNotification = 
                                  paymentType === "split_per_person" && 
                                  message?.message?.includes("wants to pay for their own items");
                                
                                if (isIndividualPaymentNotification) {
                                  return false;
                                }

                                return shouldShowPayButton;
                              })() && (
                                <Button
                                  variant="contained"
                                  size="small"
                                  disabled={
                                    message?.orderId?.paymentFlow?.status ===
                                    "pending_approval"
                                  }
                                  sx={{
                                    backgroundColor:
                                      message?.orderId?.paymentFlow?.status ===
                                      "pending_approval"
                                        ? "#D1D5DB"
                                        : "#0085FF",
                                    color: "white",
                                    fontSize: "12px",
                                    fontWeight: 600,
                                    borderRadius: 2,
                                    marginLeft: 2,
                                    px: 2,
                                    py: 0.5,
                                    textTransform: "none",
                                    minWidth: "60px",
                                    height: "28px",
                                    "&:hover": {
                                      backgroundColor:
                                        message?.orderId?.paymentFlow
                                          ?.status === "pending_approval"
                                          ? "#D1D5DB"
                                          : "#0066CC",
                                    },
                                    "&:disabled": {
                                      backgroundColor: "#D1D5DB",
                                      color: "#9CA3AF",
                                    },
                                  }}
                                  onClick={() => {
                                    if (
                                      message?.orderId?.paymentFlow?.status !==
                                        "pending_approval" &&
                                      message?.orderId?._id
                                    ) {
                                      onViewOrder?.(message);
                                    }
                                  }}
                                >
                                  {(() => {
                                    const paymentType =
                                      message?.orderId?.paymentType;
                                    const paymentFlowStatus =
                                      message?.orderId?.paymentFlow?.status;
                                    const initiatedBy =
                                      message?.orderId?.paymentFlow
                                        ?.initiatedBy;
                                    const isInitiator =
                                      initiatedBy === currentUserId;
                                    // add local responses for button label logic
                                    const responses =
                                      message?.orderId?.paymentFlow?.request
                                        ?.responses || [];

                                    // Get current user's order for button text logic
                                    const userOrder =
                                      message?.orderId?.groupOrder?.userOrders?.find(
                                        (order) =>
                                          order.userId === currentUserId
                                      );
                                    const userAmountToPay =
                                      userOrder?.amountToPay || 0;
                                    const currencyCode = message?.orderId?.currency;
                                    const currencySymbol = getCurrencySymbol(currencyCode);

                                    // For rejected pay_for_all, show individual amount
                                    const isRejectedPayForAll =
                                      paymentType === "pay_for_all" &&
                                      paymentFlowStatus === "rejected";

                                    // For approved pay_for_all initiator, show group upfront amount
                                    const isApprovedPayForAllInitiator =
                                      paymentType === "pay_for_all" &&
                                      paymentFlowStatus === "approved" &&
                                      isInitiator;

                                    // For approved pay_for_all initiator with amount to pay, show upfront amount
                                    const isApprovedPayForAllInitiatorWithAmount =
                                      paymentType === "pay_for_all" &&
                                      paymentFlowStatus === "approved" &&
                                      isInitiator &&
                                      userAmountToPay > 0;

                                    // For approved pay_for_all where current user rejected, show individual amount
                                    const userRejectedPayForAll =
                                      paymentType === "pay_for_all" &&
                                      paymentFlowStatus === "approved" &&
                                      responses.some(
                                        (response) =>
                                          response.userId === currentUserId &&
                                          response.response === "rejected"
                                      );

                                    if (isRejectedPayForAll) {
                                      // For rejected pay_for_all, show individual upfront amount for both users
                                      const userOrder =
                                        message?.orderId?.groupOrder?.userOrders?.find(
                                          (order) =>
                                            order.userId === currentUserId
                                        );
                                      const individualAmount =
                                        userOrder?.subtotal || 0;
                                      const upfrontPercentage =
                                        message?.orderId?.paymentSettings
                                          ?.upfrontPercentage;

                                      if (
                                        upfrontPercentage &&
                                        upfrontPercentage > 0
                                      ) {
                                        const upfrontAmount =
                                          (individualAmount *
                                            upfrontPercentage) /
                                          100;
                                        return `pay ${currencySymbol}${upfrontAmount.toFixed(
                                          2
                                        )}`;
                                      } else {
                                        return `pay ${currencySymbol}${individualAmount.toFixed(
                                          2
                                        )}`;
                                      }
                                    } else if (isApprovedPayForAllInitiator) {
                                      // For approved pay_for_all initiator, show group upfront amount
                                      const totalAmount =
                                        message?.orderId?.groupSubtotal || 0;
                                      const upfrontPercentage =
                                        message?.orderId?.paymentSettings
                                          ?.upfrontPercentage;

                                      if (
                                        upfrontPercentage &&
                                        upfrontPercentage > 0
                                      ) {
                                        const upfrontAmount =
                                          (totalAmount * upfrontPercentage) /
                                          100;
                                        return `pay ${currencySymbol}${upfrontAmount.toFixed(
                                          2
                                        )}`;
                                      } else {
                                        return `pay ${currencySymbol}${totalAmount.toFixed(2)}`;
                                      }
                                    } else if (
                                      isApprovedPayForAllInitiatorWithAmount
                                    ) {
                                      // For approved pay_for_all initiator with amount to pay, show upfront amount
                                      const userOrder =
                                        message?.orderId?.groupOrder?.userOrders?.find(
                                          (order) =>
                                            order.userId === currentUserId
                                        );
                                      const individualAmount =
                                        userOrder?.amountToPay || 0;
                                      const upfrontPercentage =
                                        message?.orderId?.paymentSettings
                                          ?.upfrontPercentage;

                                      if (
                                        upfrontPercentage &&
                                        upfrontPercentage > 0
                                      ) {
                                        const upfrontAmount =
                                          (individualAmount *
                                            upfrontPercentage) /
                                          100;
                                        return `pay ${currencySymbol}${upfrontAmount.toFixed(
                                          2
                                        )}`;
                                      } else {
                                        return `pay ${currencySymbol}${individualAmount.toFixed(
                                          2
                                        )}`;
                                      }
                                    } else if (userRejectedPayForAll) {
                                      // For approved pay_for_all where user rejected, show individual upfront amount
                                      const userOrder =
                                        message?.orderId?.groupOrder?.userOrders?.find(
                                          (order) =>
                                            order.userId === currentUserId
                                        );
                                      const individualAmount =
                                        userOrder?.subtotal || 0;
                                      const upfrontPercentage =
                                        message?.orderId?.paymentSettings
                                          ?.upfrontPercentage;

                                      if (
                                        upfrontPercentage &&
                                        upfrontPercentage > 0
                                      ) {
                                        const upfrontAmount =
                                          (individualAmount *
                                            upfrontPercentage) /
                                          100;
                                        return `pay ${currencySymbol}${upfrontAmount.toFixed(
                                          2
                                        )}`;
                                      } else {
                                        return `pay ${currencySymbol}${individualAmount.toFixed(
                                          2
                                        )}`;
                                      }
                                    }

                                    return "pay";
                                  })()}
                                </Button>
                              )}
                          </Box>
                        </Box>
                      </Box>
                    ) : message?.type === "order" ? (
                      // order message layout - structured display similar to first image
                      // displays order information in a card-like format with image placeholder and order details
                      <OrderMessage
                        message={message}
                        theme={theme}
                        onViewOrder={onViewOrder}
                      />
                    ) : message?.type === "order_cancellation_request" ? (
                      <OrderCancellationRequestMessage
                        message={message}
                        onViewOrder={onViewOrder}
                        getCancelledItemCount={getCancelledItemCount}
                      />
                    ) : (
                      // normal mode - show message text
                      <Box sx={{ width: "100%" }}>
                        {(() => {
                          const messageText = message?.type === "payment-request" ||
                            message?.type === "payment_request"
                            ? generatePaymentMessageText()
                            : formattedMessage.text;
                          
                          const isStructured = hasStructuredContent(messageText);
                          
                          // Check if AI is streaming and text is empty or very short
                          const isAiStreaming = message?.isAiStreaming === true;
                          // Show loader if AI is streaming and either no text or text is very short (less than 5 characters)
                          const showTypingLoader = isAiStreaming && (!messageText || !messageText.trim() || messageText.trim().length < 5);
                          
                          return (
                            <Typography
                              variant="body2"
                              component="div"
                              sx={{
                                color: isOwn ? "#FFFFFF" : "#000000",
                                fontSize: "15px",
                                fontWeight: 400,
                                whiteSpace: isStructured ? "pre-wrap" : "normal",
                                wordBreak: "break-word",
                                lineHeight: isStructured ? 1.45 : 1.5,
                                textAlign: "left",
                                "& strong": {
                                  fontWeight: 700,
                                  color: "inherit",
                                },
                                "& h2": { margin: "6px 0 2px 0", fontSize: "1.05em" },
                              }}
                            >
                              {showTypingLoader ? (
                                <TypingDotsLoader isOwn={isOwn} />
                              ) : messageText ? (
                                <>
                                  {renderMessageWithMentions(messageText)}
                                  {message?.type === "payment_request" && (
                                    <>
                                      {" "}
                                      <Box
                                        component="span"
                                        sx={{
                                          color: "#7C3AED",
                                          fontWeight: 700,
                                          textDecoration: "underline",
                                          cursor: "pointer",
                                          opacity: 1,
                                          "&:hover": {
                                            color: "#6D28D9",
                                            textDecoration: "underline",
                                          },
                                        }}
                                        onClick={() => {
                                          onOpenPaymentDialog?.(message);
                                        }}
                                      >
                                        tip here.
                                      </Box>
                                    </>
                                  )}
                                </>
                              ) : null}
                              {/* show edited indicator if message was edited */}
                              {message.isEdited && (
                                <Typography
                                  component="span"
                                  sx={{
                                    fontSize: "11px",
                                    fontStyle: "italic",
                                    opacity: 0.7,
                                    ml: 1,
                                  }}
                                >
                                  (edited)
                                </Typography>
                              )}
                            </Typography>
                          );
                        })()}
                      </Box>
                    )}
                  </>
                )}
              </>
            )}

            {message?.orderId?.status === "daft" && (
              <Typography
                variant="body2"
                sx={{ fontSize: "12px", color: "#6b7280", fontWeight: 500 }}
              >
                the order has reached the queue limit for this location. please
                try again after some time.
              </Typography>
            )}
          </MessageContent>
          {isOwn && !isDeletedForMe && !isDeletedForEveryone && !isEditing && (
            <Tooltip title="more options">
              <IconButton
                size="small"
                onClick={handleOptionsClick}
                sx={{
                  width: "24px",
                  height: "24px",
                  // color: isOwn ? "rgba(255, 255, 255, 0.9)" : "rgba(0, 0, 0, 0.7)",
                  backgroundColor: isOwn
                    ? "rgba(255, 255, 255, 0.1)"
                    : "rgba(0, 0, 0, 0.05)",
                  borderRadius: "50%",
                  "&:hover": {
                    // color: isOwn ? "rgba(255, 255, 255, 1)" : "rgba(0, 0, 0, 0.9)",
                    backgroundColor: isOwn
                      ? "rgba(255, 255, 255, 0.2)"
                      : "rgba(0, 0, 0, 0.1)",
                    transform: "scale(1.1)",
                  },
                  transition: "all 0.2s ease",
                }}
              >
                <MoreVert fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
        {/* reactions display - positioned below message, only show if not deleted and not editing */}
        {!isDeletedForMe && !isDeletedForEveryone && !isEditing && (
          <Box
            sx={{
              position: "absolute",
              right: isOwn ? 10 : 0,
              bottom: -16,
              left: !isOwn ? 0 : undefined,
            }}
          >
            {message.reactions && message.reactions.length > 0 && (
              <Box
                sx={{
                  display: "flex",
                  gap: 0.6,
                }}
              >
                {/* Show unique emojis from reactions */}
                {[...new Set(message.reactions.map((r) => r.emoji))].map(
                  (emoji, index) => (
                    <Box
                      key={`${message._id}-${emoji}-${index}`}
                      sx={{
                        fontSize: "1rem",
                        cursor: "pointer",
                        transition: "all 0.2s ease",
                        "&:hover": {
                          transform: "scale(1.1)",
                        },
                        bgcolor: "#f0f0f0",
                        width: 24,
                        height: 24,
                        borderRadius: 12,
                        alignItems: "center",
                        justifyContent: "center",
                        display: "flex",
                      }}
                    >
                      {emoji}
                    </Box>
                  )
                )}
              </Box>
            )}
          </Box>
        )}
      </Box>

      {/* location displayed below the message box (sender location; skip when type is "location" – we show the card above) */}
      {message.location &&
        message.location !== "null" &&
        message.location !== null &&
        message.location.trim() !== "" &&
        message.type !== "location" && (
          <Box
            onClick={() =>
              onViewLocation &&
              message.type === "location" &&
              onViewLocation(message)
            }
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              mt: 0.5,
              alignSelf: isOwn ? "flex-end" : "flex-start",
              maxWidth: "100%",
              cursor:
                message.type === "location" && onViewLocation
                  ? "pointer"
                  : "default",
              "&:hover":
                message.type === "location" && onViewLocation
                  ? {
                      opacity: 0.7,
                    }
                  : {},
            }}
          >
            <LocationIcon
              sx={{
                fontSize: { xs: 12, sm: 14 },
                color: isOwn ? "#6B7280" : "#000000", // gray for own messages, black for others
              }}
            />
            <Typography
              variant="caption"
              sx={{
                color: isOwn ? "#6B7280" : "#000000", // gray for own messages, black for others
                fontSize: { xs: "0.7rem", sm: "0.75rem" },
                fontWeight: 400,
              }}
            >
              {message.location}
            </Typography>
          </Box>
        )}

      {/* message footer with timestamp and status */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: isOwn ? "flex-end" : "flex-start",
          gap: 1,
          mt: 3,
          width: "100%",
        }}
      >
        {/* timestamp */}
        <Typography
          variant="caption"
          sx={{
            color: "rgba(0,0,0,0.6)",
            fontSize: { xs: "0.7rem", sm: "0.75rem" },
          }}
        >
          {message.timestamp}
        </Typography>

        {/* message status indicators for own messages */}
        {isOwn && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
            }}
          >
            {message.isTemp ? (
              <AccessTime sx={{ fontSize: "0.9rem", color: "rgba(0,0,0,0.4)" }} />
            ) : (
              getReadReceiptStatus()
            )}
          </Box>
        )}

        {/* three-dot options button - shows for all messages */}
      </Box>

      {/* options menu popover */}
      <Popover
        open={Boolean(optionsAnchorEl)}
        anchorEl={optionsAnchorEl}
        onClose={handleOptionsClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: isOwn ? "right" : "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: isOwn ? "right" : "left",
        }}
        PaperProps={{
          elevation: 3,
          sx: {
            borderRadius: 2,
            minWidth: "180px",
          },
        }}
      >
        <List sx={{ py: 0 }}>
          {/* only show forward option if onForwardMessage handler is provided */}
          {onForwardMessage && (
            <ListItem onClick={() => handleMenuOption("forward")}>
              <ListItemIcon>
                <Forward fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="forward" />
            </ListItem>
          )}

          <ListItem onClick={() => handleMenuOption("reply")}>
            <ListItemIcon>
              <Reply fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="reply" />
          </ListItem>

          {/* only show "edit" option for own messages AND if editing is allowed by business chat settings */}
          {isOwn && isEditingAllowed() && (
            <ListItem onClick={() => handleMenuOption("edit")}>
              <ListItemIcon>
                <Edit fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="edit" />
            </ListItem>
          )}

          {isOwn && isDeletingAllowed() && (
          <ListItem onClick={() => handleMenuOption("delete for me")}>
            <ListItemIcon>
                <Delete fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="delete for me" />
            </ListItem>
          )}

          {/* only show "delete for everyone" option for own messages AND if deleting is allowed by business chat settings */}
          {isOwn && isDeletingAllowed() && (
            <ListItem onClick={() => handleMenuOption("delete for everyone")}>
              <ListItemIcon>
                <Delete fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="delete for everyone" />
            </ListItem>
          )}

          {/* only show "info" option for own messages */}
          {isOwn && (
            <ListItem onClick={() => handleMenuOption("info")}>
              <ListItemIcon>
                <Info fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="info" />
            </ListItem>
          )}
        </List>
      </Popover>

      {/* emoji picker popover */}
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "top",
          horizontal: isOwn ? "left" : "right", // own messages open left, others open right
        }}
        transformOrigin={{
          vertical: "bottom",
          horizontal: isOwn ? "left" : "right", // own messages open left, others open right
        }}
        PaperProps={{
          elevation: 0,
          sx: {
            backgroundColor: "transparent",
            boxShadow: "none",
          },
        }}
        // Force popover to stay within chat view boundaries
        slotProps={{
          paper: {
            sx: {
              maxWidth: "250px",
              overflow: "hidden",
              marginLeft: isOwn ? "0" : "auto", // left messages push right, own messages no margin
              marginRight: isOwn ? "auto" : "0", // own messages push left, left messages no margin
            },
          },
        }}
      >
        <EmojiPicker>
          {/* 5 emoji buttons */}
          {emojis.map((emoji, index) => (
            <Tooltip key={index} title={`react with ${emoji}`}>
              <EmojiButton
                onClick={() => handleEmojiSelect(emoji)}
                size="small"
              >
                {emoji}
              </EmojiButton>
            </Tooltip>
          ))}

          {/* plus icon for more emojis */}
          <Tooltip title="more emojis">
            <EmojiButton
              onClick={handlePlusClick}
              size="small"
              sx={{
                color: "#6B7280",
                "&:hover": {
                  backgroundColor: "#f3f4f6",
                },
              }}
            >
              <Add fontSize="inherit" />
            </EmojiButton>
          </Tooltip>
        </EmojiPicker>
      </Popover>

      {/* extended emoji picker */}
      {showExtendedEmoji && (
        <Popover
          open={showExtendedEmoji}
          anchorEl={anchorEl}
          onClose={() => setShowExtendedEmoji(false)}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: isOwn ? "right" : "left",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: isOwn ? "right" : "left",
          }}
          PaperProps={{
            elevation: 0,
            sx: {
              backgroundColor: "transparent",
              boxShadow: "none",
            },
          }}
        >
          <Paper
            sx={{
              width: "320px",
              maxHeight: "400px",
              backgroundColor: "#ffffff",
              borderRadius: 2,
              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
              overflow: "hidden",
              mt: 1, // Add margin top for spacing below message
            }}
          >
            {/* Search bar */}
            <Box sx={{ p: 2, borderBottom: "1px solid #e5e7eb" }}>
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                placeholder="Search emojis..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 1,
                  },
                }}
              />
            </Box>

            {/* Category tabs */}
            <Box
              sx={{
                display: "flex",
                gap: 0.5,
                p: 1,
                borderBottom: "1px solid #e5e7eb",
                overflowX: "auto",
                "&::-webkit-scrollbar": {
                  height: "4px",
                },
                "&::-webkit-scrollbar-track": {
                  backgroundColor: "#f1f1f1",
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "#c1c1c1",
                  borderRadius: "2px",
                },
              }}
            >
              {Object.keys(extendedEmojis).map((category) => (
                <Button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  size="small"
                  sx={{
                    px: 2,
                    py: 0.5,
                    minWidth: "auto",
                    borderRadius: 1,
                    fontSize: "0.75rem",
                    fontWeight: selectedCategory === category ? "600" : "400",
                    backgroundColor:
                      selectedCategory === category ? "#f3f4f6" : "transparent",
                    color:
                      selectedCategory === category ? "#7C3AED" : "#6B7280",
                    "&:hover": {
                      backgroundColor: "#f3f4f6",
                      color: "#7C3AED",
                    },
                  }}
                >
                  {category}
                </Button>
              ))}
            </Box>

            {/* Emoji grid */}
            <Box
              sx={{
                p: 1,
                display: "grid",
                gridTemplateColumns: "repeat(8, 1fr)",
                gap: 0.5,
                maxHeight: "300px",
                overflowY: "auto",
                "&::-webkit-scrollbar": {
                  width: "6px",
                },
                "&::-webkit-scrollbar-track": {
                  backgroundColor: "#f1f1f1",
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "#c1c1c1",
                  borderRadius: "3px",
                },
              }}
            >
              {getFilteredExtendedEmojis().map((emoji, index) => (
                <Tooltip key={index} title={`react with ${emoji}`}>
                  <EmojiButton
                    onClick={() => handleExtendedEmojiSelect(emoji)}
                    size="small"
                    sx={{
                      width: "32px",
                      height: "32px",
                      fontSize: "1.1rem",
                    }}
                  >
                    {emoji}
                  </EmojiButton>
                </Tooltip>
              ))}
            </Box>
          </Paper>
        </Popover>
      )}

      {/* message info dialog - shows read and delivered status like whatsapp */}
      <MessageInfoDialog
        open={messageInfoDialogOpen}
        onClose={() => setMessageInfoDialogOpen(false)}
        message={message}
      />
    </MessageBubbleContainer>
    </>
  );
};

export default MessageBubble;
